import * as Sentry from "@sentry/browser";
import memoize from "memoize-one";
import merge from "merge";
import { ColDef, ColGroupDef, Column, ColumnGroup, GridApi, MenuItemDef, ValueGetterParams } from "ag-grid-community";
import { Iterable, List, Set as ImmutableSet } from "immutable";
import { formatMessage } from "lib/globalize-util";
import { getAgGridColumnWidth } from "lib/text";
import lodashSet from "lodash/set";
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import isEmpty from "lodash/isEmpty";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashUniqWith from "lodash/uniqWith";
import lodashIsEqual from "lodash/isEqual";
import lodashMemoize from "lodash/memoize";
import { Door, Editors, GridParamType, Item, Kinds, UnitsEntryType, ValueTypes } from "constants/product-collection";
import {
    collectionMathJs,
    getCellRendererSelector,
    IDisplayFields,
    ISchemaKey,
    mathJsSymbolIsField,
} from "helpers/product-collection";
import {
    AllocationBag,
    AllocationBagType,
    AllocationDimensionIndex,
    getAllocationBagValue,
    getAllocationDimensionByIndex,
    getUserDefinedClassificationName,
    IAllocationDimension,
    IAllocationDimensionValue,
    ROLLUP_ALLOCATION_DIMENSION_DELIMITER,
} from "helpers/allocation-dimension";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { ValueOf } from "lib/ts-utility";
import { escapeHTML, mergedCellClass } from "helpers/ag-grid";
import {
    ColumnTypes,
    defaultColumnDefsValueSetterCreator,
    grandTotalColumnValueSetterCreator,
    MetricsTotalType,
} from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import {
    IFilterConditionQueryParam,
    IGridParamsQueryParam,
    IPreference,
} from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels/data-view-panel/grid-param-helpers";
import {
    getPlanRowType,
    ICurrency,
    PlanMetricRows,
    TotalTypes,
} from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import {
    ASSORTMENT_TOTALS_ROW,
    getPlan,
    getTotal,
} from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers/pinned-row-helpers";
import { pinnedValueFormatter } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-formatters/pinned-formatter";
import { tableValueGetter } from "./table-bag";
import {
    BULK_IDENTIFIER,
    DEFAULT_VALUES_PINNED_ROW,
    detailColumnValueGetterCreator,
    detailColumnValueSetterCreator,
    SIZE_IDENTIFIER,
    SIZE_TOTALS_IDENTIFIER,
} from "components/product-collection/product-collection-views/product-collection-grid-view/detail-list/helpers";
import {
    CellClassParams,
    EditableCallback,
    EditableCallbackParams,
    IsColumnFuncParams,
    ValueSetterParams,
} from "ag-grid-community/dist/lib/entities/colDef";
import { DATATEST } from "constants/integration";
import { isValidNumber } from "lib/number";
import { nullish } from "./nullish";
import { CopyCutPasteActions, UnitsPath } from "./product-collection/units-copy-paste";
import { EditableMetrics, Metric, Metrics, MetricsKind } from "helpers/metrics/types";
import { metricsValueGetterCreator } from "./metrics/getter-setter-factory";
import { get, last } from "lodash";
import { generateMetricColId, getMetricColIdSuffix } from "./metrics/column-formula/ag-grid-helper";
import { getMetricsValueFormatter } from "./metrics/formatters/metrics-formatter-helper";
import { theme } from "styles/theme";
import {
    IAttributeFilters,
    IDimensionFilter,
} from "components/product-collection/product-collection-toolbar/product-collection-toolbar-filters/types";
import { Attribute, Dimension, DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { getLegacyTypeByMetricsKind, getMetricKindByLegacyType, getTotalTypesLabel } from "./metrics/legacy-metrics";
import { getInitialState as getDimensionsPanelEmptyState } from "reducers/dimensions-panel/applied/initial-state";
import { customSort } from "./array-utils";
import { IGridContext } from "components/product-collection/product-collection-views";

export interface IDoorTotal {
    fieldName: string;
    currency: {
        symbol: string;
        decimal_places: number;
    };
    formula?: string;
}

export interface IDoor {
    id: string;
    value: string;
    label: string;
    classifications:
        | {
              [classification: string]: string;
          }
        | {};
    retail?: IDoorTotal;
    wholesale?: IDoorTotal;
    totalConversionRate?: number;
    doorNotes?: string;
    groupedDoorsNumber?: number;
}

interface IClassification {
    id: string;
    label: string;
    totalCols: string[][];
    columns: ColDef[];
}

export interface IAllocationDimensionViewConfig {
    // `undefined` means no config specified (Use default)
    dimensionId: string;
    values: string[] | undefined;
    direction: Order | undefined;
}

export type IAllocationDimensionColumns = IAllocationDimensionViewConfig[];

export interface IDoorGroup {
    field: string;
    values: string[];
    hiddenValues?: string[];
    direction: Order;
}

export interface IHideClassification {
    key: string[];
}

type AllocationDimensionHashId = "deliveryIds" | "doorIds";
export type AllocationDimensionHash = {
    [allocationDimensionId: string]: AllocationDimensionHashId;
};

export interface IClasificationHierarchy {
    field: string;
    value: string;
}

// Returns a hash of allocationIds and the values are the property name of IAllocationDimensionColumns
export const getAllocationDimensionHash = (allocationDimensions: IAllocationDimension[]): AllocationDimensionHash => {
    const groupAllocationDimension = getAllocationDimensionByIndex(allocationDimensions, AllocationDimensionIndex.Date);
    const doorAllocationDimension = getAllocationDimensionByIndex(allocationDimensions, AllocationDimensionIndex.Door);

    // This is to tie the dynamic with the hardcoded
    return {
        ...(groupAllocationDimension && { [groupAllocationDimension.id]: "deliveryIds" }),
        ...(doorAllocationDimension && { [doorAllocationDimension.id]: "doorIds" }),
    };
};

export const SR_CUSTOM_DOOR_GROUPING_DELIMITER = ROLLUP_ALLOCATION_DIMENSION_DELIMITER;
export const SR_CUSTOM_DOOR_GROUPING_DIRECTION_DELIMITER = "===";
/**
 * Checks if a field name is a door
 */
export const DOOR_FIELD_PREFIX = "door_";
export const isDoorFieldName = (fieldName) => {
    // Door id can be numeric now, which is a problem as Math.js parses it as number.
    // As temporary workaround, we prefix that by 'door_' in schema.
    // TODO: remove this when we are able to reference allocation dimension better.
    return fieldName.includes(DOOR_FIELD_PREFIX);
};

export const GROUP_ID_DELIMITER = "_";

export const DEFAULT_DOOR_GOA = "DEFAULT_DOOR_GOA";

export const getGroupId = (delimiter, ...groups: string[]) => {
    return encodeURIComponent(groups.join(delimiter));
};

const COL_ID_DELIMITER = "_";

export const parseDimensionColumn = (colId: string, deliveryIds: string[]) => {
    if (colId.startsWith(ColumnTypes.DoorBulkQuantity)) {
        const [node1, node2] = colId.replace(ColumnTypes.DoorBulkQuantity, "").split(COL_ID_DELIMITER).splice(1);

        const hasDelivery = !nullish(node2);
        const doorId = hasDelivery ? node2 : node1;
        const deliveryId = hasDelivery ? node1 : undefined;
        return { doorId, deliveryId };
    }

    if (colId.startsWith(ColumnTypes.AllDimensionTotal)) {
        return { isAllDimensionTotal: true };
    }

    if (colId.startsWith(ColumnTypes.AllocationDimensionTotal)) {
        const [deliveryIndex] = colId
            .replace(ColumnTypes.AllocationDimensionTotal, "")
            .split(COL_ID_DELIMITER)
            .splice(1);
        return { isDimensionTotal: true, deliveryId: deliveryIds[deliveryIndex] };
    }

    return null;
};

export const getColIdByColType = (colType: ColumnTypes, idsHierarcy: Array<string | number>) => {
    const flatId = [colType as string].concat(idsHierarcy as string[]).join(COL_ID_DELIMITER);

    return flatId;
};

const cellClassToAlignRight = { "justify-content": "flex-end" };
const cellClassToAlignRightNotEditable = {
    "justify-content": "flex-end",
    color: "#a5a0a0",
    backgroundColor: theme.grid.headerBackgroundColor,
};
const cellClassForLeftSeparator = { "border-left": `2px solid ${theme.grid.sectionSeparatorColor}` };

export const PURCHASE_ORDER_DOOR_GROUP_FIELD = "purchase_order_door_group";
export const NUMBER_OF_DOORS = Door.FIELDS.NUMBER_OF_DOORS[1];

// Door classification label needs to be appended to header title.
// Unfortunately, Ag grid doesn't allow ability to pass header title as HTML markup.
// To achieve that, we modify header template as suggested here (https://www.ag-grid.com/javascript-grid-column-header/#header-templates).
// Or alternatively, we can create dustom header component, but it will add maintenance overhead
// as we have to maintain sort, filter and menu logic (https://www.ag-grid.com/javascript-grid-header-rendering/#grid-vs-your-responsibilities).
const generateHeaderWithClassificationLabelTemplate = (classification: string) => {
    return `<div class='ag-cell-label-container' role='presentation'>
        <span ref='eMenu' class='ag-header-icon ag-header-cell-menu-button' aria-hidden='true'></span>
        <div ref='eLabel' class='ag-header-cell-label' role='presentation' unselectable='on'>
            <span ref='eText' class='ag-header-cell-text' role='columnheader' unselectable='on'></span>
            <span class='doorsGroupClassification'>(${classification})</span>
            <span ref='eFilter' class='ag-header-icon ag-filter-icon' aria-hidden='true'></span>
            <span ref='eSortOrder' class='ag-header-icon ag-sort-order' aria-hidden='true'></span>
            <span ref='eSortAsc' class='ag-header-icon ag-sort-ascending-icon' aria-hidden='true'></span>
            <span ref='eSortDesc' class='ag-header-icon ag-sort-descending-icon' aria-hidden='true'></span>
            <span ref='eSortNone' class='ag-header-icon ag-sort-none-icon' aria-hidden='true'></span>
        </div>
    </div>`;
};

export const BLANK_CLASSIFICATION_VALUE = "na";

export interface ICreatorParams {
    isTotalColumn: boolean;
    breadcrumb: string[] | string[][];
    sizes?: string[];
    groupedDoorsNumber?: number;
}
type IValueGetterCreator = (params: ICreatorParams) => ColDef["valueGetter"];
type IValueSetterCreator = (params: ICreatorParams) => ColDef["valueSetter"];

const isAnyDoorVisible = lodashMemoize((columns: Column[]) =>
    columns.some((col) => col.getColDef().type === ColumnTypes.DoorBulkQuantity)
);

const isMetricCellEditable = (params: EditableCallbackParams, isEditable: boolean): boolean => {
    const { data, colDef, columnApi } = params;
    const context = params.context as IGridContext;

    if (data?.type === DEFAULT_VALUES_PINNED_ROW) {
        return false;
    }

    if (colDef?.cellRendererParams?.isServerSide) {
        return false;
    }

    if (data?.planType) {
        return false;
    }

    if (data?.type === SIZE_IDENTIFIER) {
        return false;
    }

    if (data?.totalType === ASSORTMENT_TOTALS_ROW) {
        return false;
    }
    // assortment-manage-distribution flag condition
    // remove this condition when we remove the flag
    const isDistributionEnabledFlag = context?.isDistributionEnabled;
    const metricKind = colDef.cellRendererParams.metric.kind;
    const isClassificationColumn = colDef.type === ColumnTypes.ClassificationTotal;
    const editableMetrics = [MetricsKind.UNITS, MetricsKind.AVERAGE_UNITS];
    const isEditableMetric = editableMetrics.includes(metricKind);

    if (isClassificationColumn && isDistributionEnabledFlag && isEditableMetric) {
        // Worst case scenario is O(n) where n is the number of columns in the grid
        // wasn't able to use `dimensionFilters` + `dimensionPanelApplied` states
        // because the later was not being updated inside the context on changes
        // TODO watch performance on this
        const columns = columnApi.getAllDisplayedColumns();

        if (!isAnyDoorVisible(columns)) {
            return true;
        }

        const parentColumn = params.column.getParent();
        const isColumnEditable =
            parentColumn.getChildren().filter((col: Column) => col.getColDef().type === ColumnTypes.DoorBulkQuantity)
                .length > 0;

        return isColumnEditable;
    }

    // This is important for override.
    if (isDistributionEnabledFlag && isEditableMetric) {
        return true;
    }

    return isEditable;
};

const isMetricEditable = (params: EditableCallbackParams, metric: Metric) => {
    const { data } = params;

    const isTotalCell = data?.totalType;
    const isPlanCell = data?.planType;

    if (isTotalCell) {
        return false;
    }

    if (isPlanCell) {
        return true;
    }

    const isMetricEditable = EditableMetrics.includes(metric.kind);

    return isMetricEditable;
};

export const getPinnedRowEditable = (editable: ColDef["editable"], params: IsColumnFuncParams): boolean => {
    const { node, colDef, api, data } = params;
    const { type } = colDef;
    let rowEditable = typeof editable === "function" ? editable(params) : editable;

    if (colDef.cellRendererParams?.isServerSide) {
        return false;
    }

    if (node.rowPinned && rowEditable) {
        rowEditable = false;
        const rowCount = api.getPinnedTopRowCount();
        const showTotals = Object.keys(TotalTypes).length === rowCount; // if number of pinned rows is the same lenght as number of total types, then we're displaying totals
        if (!showTotals) {
            // should only allow editable cell if pinned rows are plan types (not total types)
            // and only if it's a plan type row
            // and only for door bulk quantity cells
            const planType = getPlanRowType(node.rowIndex);
            if (planType === PlanMetricRows.Plan && type === ColumnTypes.DoorBulkQuantity) {
                rowEditable = true;
            }
        }

        if (data?.type === DEFAULT_VALUES_PINNED_ROW) {
            rowEditable = false;
        }
    }

    return rowEditable;
};

const getCellClassBasedOnColType = (params: CellClassParams) => {
    const classes = [];
    if (params.colDef.type) {
        classes.push(`${params.colDef.type}-column`);
    }
    return classes;
};

const getCellClassBasedOnRowType = (params: CellClassParams) => {
    const classes = [];

    const isGrandTotalRow = get(params, "data.totalType") === ASSORTMENT_TOTALS_ROW;
    if (isGrandTotalRow) {
        classes.push("totalsRow");
    }

    const isTargetRow = !!params.data?.targetMetricsType;
    if (isTargetRow) {
        classes.push("targetRow");
    }

    return classes;
};

const getCellClassBasedOnEditable = (params: CellClassParams, colId: string) => {
    const column = params.columnApi ? params.columnApi.getColumn(colId) : undefined;
    const isEditable = column && column.isCellEditable(params.node);

    return [isEditable ? "doorsGroupTotal" : "doorsGroupTotal--notEditable"];
};

const getCellClassForMetricBasedOnEditable = (params: CellClassParams, colId: string, totalType?: MetricsTotalType) => {
    const column = params.columnApi ? params.columnApi.getColumn(colId) : undefined;
    const isEditable = column && column.isCellEditable(params.node);

    const classes = [];
    if (!isEditable) {
        classes.push("doorsGroupTotal--notEditable");
    }

    if (totalType === MetricsTotalType.GrandTotalMetric) {
        classes.push("notEditable");
    }

    return classes;
};

const getCellClassForDoorsGroupTotalBasedOnEditable = (
    params: CellClassParams,
    colId: string,
    editable: boolean | EditableCallback
) => {
    const column = params.columnApi ? params.columnApi.getColumn(colId) : undefined;
    const isEditable =
        typeof editable === "boolean"
            ? editable
            : // It's weird sometimes column is null
              column && column.isCellEditable(params.node);

    const isPinned = params.node.rowPinned;
    return [
        isEditable ? "doorsGroupTotal" : "doorsGroupTotal--notEditable",
        isPinned ? "doorsGroupTotal--pinned" : null,
    ];
};

interface IBuildDoorColumn {
    headerName: string;
    breadcrumbs: string[];
    groupId?: string;
    doorId?: string;
    classificationName?: string;
    classificationValue?: string;
    classificationLabel?: string;
    valueGetterCreator: IValueGetterCreator;
    valueSetterCreator: IValueSetterCreator;
    editable?: ColDef["editable"];
    reviewPlan?: any;
    cellClass?: ColDef["cellClass"];
    movable?: boolean;
    door?: IDoor;
    hide?: boolean;
    metric?: Metric;
    sortable?: boolean;
    totalType?: MetricsTotalType;
    merchantCurrency?: ICurrency;
    filteredDeliveriesForAssortmentSet?: Set<string>;
    filteredDoorsForAssortmentSet?: Map<string, IDoor>;
    delimiter?: string;
    classificationHierarchy?: IClasificationHierarchy[];
    useSize?: boolean;
    rootGridApi?: GridApi;
    currentLayout?: IPreference[];
}
export enum RowRenderers {
    CUSTOM_PINNED_ROW_RENDERER = "customPinnedRowRenderer",
}

/**
 * Merges display properties' field names and fields' values into an object
 */
export const getClassifications = (displayProperties: ISchemaKey[], fields) => {
    return displayProperties.reduce((acc, displayProperty) => {
        if (displayProperty.kind !== Kinds.ALLOCATION_DIMENSION_CLASSIFICATION) {
            return acc;
        }

        const fieldName = displayProperty.collectionId
            ? `${displayProperty.field_name}${ROLLUP_ALLOCATION_DIMENSION_DELIMITER}${displayProperty.collectionId}`
            : displayProperty.field_name;

        const classificationValue =
            displayProperty.value_type === ValueTypes.NUMBER
                ? lodashGet(fields, [fieldName, "number_value"], undefined)
                : lodashGet(fields, [fieldName, "string_value"], "").trim();

        if (classificationValue) {
            acc[fieldName] = classificationValue;
        }
        return acc;
    }, {});
};

/**
 * Returns a list of empty column objects based on a list of allowed display properties
 */
export const rowsFromDisplayProperties = (displayProperties): { [key: string]: IClassification } => {
    return displayProperties.reduce((acc, displayProperty) => {
        const classificationValue = displayProperty.id.trim();
        if (classificationValue) {
            acc[classificationValue] = {
                id: displayProperty.id,
                label: displayProperty.label,
                totalCols: [],
                columns: [],
            };
        }
        return acc;
    }, {});
};

const editableIfHasSize = (size: string, params: IsColumnFuncParams) => {
    const { sizes } = params.data;
    return (sizes as Set<string>)?.has(size);
};

const grayIfHasNoSize = (size: string, params: CellClassParams) => {
    const { sizes } = params.data;
};

export const amountOfNotTotalChildren = (node: ColGroupDef | ColDef) => {
    const { children } = node as ColGroupDef;
    const TOTALS_TYPE_SET = ImmutableSet<string>([
        ColumnTypes.AllDimensionTotal,
        ColumnTypes.ClassificationTotal,
        ColumnTypes.AllocationDimensionTotal,
    ]);
    return (
        children?.reduce((acc, curr: ColDef | ColGroupDef) => {
            const currAsColDef = curr as ColDef;
            const currAsColGroupDef = curr as ColGroupDef;
            const typeOrGroupId = currAsColDef.type || currAsColGroupDef.groupId;
            if (TOTALS_TYPE_SET.has(typeOrGroupId as string)) {
                return acc;
            }
            return acc + 1;
        }, 0) || 0
    );
};

export const wrapChildrenWithNoSiblings = (node: ColGroupDef | ColDef) => {
    const nodeAsGroup = node as ColGroupDef;
    const { children } = nodeAsGroup;
    const notTotalChildren = amountOfNotTotalChildren(node);
    if (notTotalChildren === 1) {
        const onlyChild = children[children.length - 1];
        const grandchilds = (onlyChild as ColGroupDef).children;
        if (grandchilds) {
            nodeAsGroup.children = grandchilds;
            wrapChildrenWithNoSiblings(node);
        }
    } else if (notTotalChildren > 1) {
        children.forEach(wrapChildrenWithNoSiblings);
    }
};

const sizeValueSetterCreator = (creatorParams) => {
    const { breadcrumb, sizes, hasAllocationBag } = creatorParams;
    const originalSize = breadcrumb[breadcrumb.length - 1];
    const originalBreadcrumb = breadcrumb.slice(0, breadcrumb.length - 1);
    const allocationSetter = detailColumnValueSetterCreator(creatorParams);
    const bulkSetter = detailColumnValueSetterCreator({
        ...creatorParams,
        breadcrumb: originalBreadcrumb,
        sizes: [],
        hasAllocationBag,
    });
    return (params: ValueSetterParams) => {
        const { oldValue, newValue, data, context } = params;
        const cleanedNewValue = isValidNumber(newValue) ? newValue : 0;
        if (newValue === oldValue) {
            return true;
        }
        const isLegacyBag = !context?.allocationBagRef;
        const newBulkValue = sizes.reduce((acc, size) => {
            let value = 0;
            if (isLegacyBag) {
                value = tableValueGetter(lodashGet(data, Item.ALLOCATIONS).current, originalBreadcrumb.concat([size]));
            } else {
                const itemId = data.rowId;
                value =
                    getAllocationBagValue(
                        AllocationBagType.allocations,
                        context.allocationBagRef.allocationBag,
                        itemId,
                        originalBreadcrumb.concat([size])
                    ) ?? 0;
            }
            return size !== originalSize ? acc + (value || 0) : acc;
        }, cleanedNewValue >>> 0);
        return allocationSetter(params) && bulkSetter({ ...params, newValue: newBulkValue });
    };
};
export const extendColDefWithSizes = (
    node: ColDef | ColGroupDef,
    sizes: string[],
    hasAllocationBag: boolean = false
) => {
    const { children } = node as ColGroupDef;
    const hasSizes = sizes && sizes.length !== 0;
    // At this point, we are sure sizes' attributes are string Sets
    children?.forEach((childNode, index) => {
        const child = childNode as ColDef;
        const childGroup = childNode as ColGroupDef;
        if (child.type === ColumnTypes.DoorBulkQuantity) {
            if (hasSizes) {
                const {
                    cellRendererParams: { groupId, doorId },
                } = child;
                const groupIdOrEmpty = groupId ? [groupId] : [];
                const sizeTotalColumn = buildTotalColumn({
                    headerName: formatMessage("|headerName| Total", { headerName: child.headerName }),
                    hierarchy: [...groupIdOrEmpty, doorId],
                    type: ColumnTypes.SizesTotal,
                    cols: [[...groupIdOrEmpty, doorId]],
                    editable: false,
                    valueGetterCreator: (params) => detailColumnValueGetterCreator({ ...params, sizes: sizes }),
                    valueSetterCreator: (params) =>
                        detailColumnValueSetterCreator({ ...params, sizes: sizes, hasAllocationBag }),
                });
                sizeTotalColumn.cellRendererParams.splitValue = true;
                children[index] = {
                    ...childNode,
                    openByDefault: true,
                    children: [
                        sizeTotalColumn,
                        ...sizes.map((size) => ({
                            columnGroupShow: "open",
                            headerName: size,
                            colId: [child.colId, size].join(COL_ID_DELIMITER),
                            valueGetter: ({ api, data, column, context }) => {
                                if (data.type === SIZE_TOTALS_IDENTIFIER) {
                                    let total = 0;
                                    api.forEachNode((rowNode) => {
                                        if (rowNode.data.type === SIZE_IDENTIFIER) {
                                            total += api.getValue(column, rowNode) || 0;
                                        }
                                    });
                                    return total;
                                }

                                const allocationBag = context?.allocationBagRef?.allocationBag;
                                const itemId = data.rowId;
                                const path = [...(groupId ? [groupId] : []), doorId, size];
                                if (!allocationBag) {
                                    const table = lodashGet(data, Item.ALLOCATIONS).current;
                                    return tableValueGetter(table, path);
                                } else {
                                    return getAllocationBagValue(
                                        AllocationBagType.allocations,
                                        allocationBag,
                                        itemId,
                                        path
                                    );
                                }
                            },
                            valueSetter: sizeValueSetterCreator({
                                breadcrumb: [...groupIdOrEmpty, doorId, size],
                                isTotalColumn: false,
                                sizes,
                                hasAllocationBag,
                            }),
                            editable: editableIfHasSize.bind(this, size),
                            cellClass: grayIfHasNoSize.bind(this, size),
                            width: 90,
                            cellEditor: Editors.NumberCellEditor,
                            cellEditorParams: {
                                isZeroValueAllowed: true,
                            },
                            headerClass: DATATEST.ASSORTMENTS.CDP.SIZE_COLUMN,
                        })),
                    ],
                };
            }
        } else if (!childGroup.children) {
            // This is another kind of column;
            children[index] = { ...childNode, editable: false };
        } else {
            extendColDefWithSizes(childGroup, sizes, hasAllocationBag);
        }
    });
};

let totalCols = [];
export const MENU_GENERAL_TAB_ONLY = ["generalMenuTab"];
interface IAllocationDimensionMenuItemsParams {
    enablePaste: boolean;
    path: UnitsPath;
    actionCallback: (u: UnitsPath, c: CopyCutPasteActions) => void;
}

export const getAllocationDimensionMenuItems = ({
    enablePaste,
    path,
    actionCallback,
}: IAllocationDimensionMenuItemsParams): MenuItemDef[] => [
    {
        name: formatMessage("Copy Units"),
        action: actionCallback.bind(null, path, CopyCutPasteActions.Copy),
    },
    {
        name: formatMessage("Cut Units"),
        action: actionCallback.bind(null, path, CopyCutPasteActions.Cut),
    },
    {
        name: formatMessage("Paste Units"),
        action: actionCallback.bind(null, path, CopyCutPasteActions.Paste),
        disabled: !enablePaste,
    },
];

const shouldHideDoor = (door: string) => (door === DEFAULT_DOOR_GOA ? true : false);

export const isFirstColumnInGroup = (
    column: Column,
    checkParent: boolean,
    columnGroupOverride?: ColumnGroup
): boolean => {
    if (!column) {
        return false;
    }

    let columnGroupToLookFor;
    if (columnGroupOverride) {
        columnGroupToLookFor = columnGroupOverride;
    } else if (checkParent) {
        const columnRootParentGroup = column.getParent()?.getParent() as ColumnGroup;
        // If it's not there attempt to get the parent
        if (columnRootParentGroup) {
            columnGroupToLookFor = columnRootParentGroup;
        } else {
            columnGroupToLookFor = column.getParent();
        }
    } else {
        columnGroupToLookFor = column.getParent();
    }

    if (columnGroupToLookFor) {
        // Get first child that isn't a column group
        let columnToLookFor = columnGroupToLookFor.getDisplayedChildren()[0] ?? null;
        if (columnToLookFor) {
            while ("groupId" in columnToLookFor) {
                columnToLookFor = columnToLookFor.getDisplayedChildren()[0] ?? null;
            }
        }

        if (columnToLookFor && "getColId" in columnToLookFor && columnToLookFor.getColId() === column.getColId()) {
            return true;
        }
    }

    return false;
};

export const isFirstDoorColumnInGroup = (column: Column) => {
    if (!column) {
        return false;
    }

    const columnGroupToLookFor = column.getParent();
    if (columnGroupToLookFor) {
        const doorColumns = columnGroupToLookFor.getDisplayedChildren().filter((c) => {
            const column = c as Column;
            return column.getColDef().type === ColumnTypes.DoorBulkQuantity;
        });

        const firstDoorColumn = doorColumns[0] as Column;
        if (firstDoorColumn) {
            return firstDoorColumn.getColId() === column.getColId();
        }
    }

    return false;
};

export const buildDoorColumn = ({
    headerName,
    breadcrumbs,
    groupId,
    doorId,
    classificationName,
    classificationValue,
    classificationLabel,
    valueSetterCreator,
    valueGetterCreator,
    editable,
    cellClass,
    movable = true,
    door,
    hide,
    metric,
    sortable = true,
    totalType,
    filteredDeliveriesForAssortmentSet,
    filteredDoorsForAssortmentSet,
    delimiter,
    classificationHierarchy,
    useSize,
    rootGridApi,
    currentLayout,
}: IBuildDoorColumn): ColDef => {
    const cleanedBreadcrumbs = breadcrumbs.filter((breadcrumb) => !!breadcrumb);
    if (!groupId) {
        totalCols = [...totalCols, ...cleanedBreadcrumbs];
    }
    let colId = getColIdByColType(ColumnTypes.DoorBulkQuantity, cleanedBreadcrumbs);
    if (metric) {
        colId = generateMetricColId(colId, metric.kind);
    }

    const savedWidth = getSavedWidth(colId, currentLayout);

    const groupedDoorsNumber = door?.groupedDoorsNumber; // Used for representing a set of doors, grouped by some criteria (e. g. Cluster)
    const colDef: ColDef = {
        headerName,
        headerClass: ({ column }) => {
            if (isFirstDoorColumnInGroup(column)) {
                return "borderLeftClass";
            }
            return "";
        },
        colId,
        type: ColumnTypes.DoorBulkQuantity,
        filter: false,
        lockVisible: true,
        cellRendererSelector: getCellRendererSelector(null, RowRenderers.CUSTOM_PINNED_ROW_RENDERER),
        // use getCellRendererParamsFn if you use more than one cell renderers
        cellRendererParams: {
            kind: Kinds.DOOR_BULK_QUANTITY,
            allocationReferenceFields: {
                retailFieldName: door?.retail?.fieldName || "us_retail",
                wholesaleFieldName: door?.wholesale?.fieldName || "us_cost",
            },
            splitValue: true,
            currency: undefined,
            groupId,
            doorId,
            classificationName,
            classificationValue,
            dimensionIds: groupId
                ? [[groupId], [doorId]]
                : filteredDeliveriesForAssortmentSet?.size === 0
                  ? [[doorId]]
                  : [getFilteredDeliveriesArray(filteredDeliveriesForAssortmentSet), [doorId]],
            getTotalHelper: (planData, gridContext) => {
                return getTotal({
                    colType: ColumnTypes.DoorBulkQuantity,
                    doorId,
                    groupId,
                    doorTotals: gridContext.doorTotals,
                    totalType: planData.totalType,
                });
            },
            getPlanHelper: (planData, gridContext) => {
                return getPlan({
                    colType: ColumnTypes.DoorBulkQuantity,
                    doorId,
                    groupId,
                    reviewPlan: planData.plan,
                    doorTotals: gridContext.doorTotals,
                    totalType: planData.totalType,
                });
            },
            metric,
        },
        pinnedRowValueFormatter: metric
            ? getMetricsValueFormatter({ kind: metric.kind, numberFormat: metric.numberFormat })
            : pinnedValueFormatter,
        suppressColumnsToolPanel: true,
        menuTabs: MENU_GENERAL_TAB_ONLY,
        suppressMovable: !movable,
        columnGroupShow: "open",
        editable: totalType === MetricsTotalType.GrandTotalMetric ? false : getPinnedRowEditable.bind(this, editable),
        width: savedWidth ?? getAgGridColumnWidth({ text: headerName }),
        cellEditor: Editors.NumberCellEditor,
        cellEditorParams: {
            isZeroValueAllowed: true,
        },
        //to hide Doors in GOA
        hide: hide || shouldHideDoor(door?.label),
        sortable: metric ? sortable : true,
        valueGetter: (getterParams: ValueGetterParams) => {
            let valueGetter;

            // Add case for CDP bulk row to use passed in value getter creator for units and avg units metric columns
            if (
                !metric ||
                (metric && getterParams.node.data?.type === BULK_IDENTIFIER && metric.kind === MetricsKind.UNITS)
            ) {
                valueGetter = valueGetterCreator({
                    isTotalColumn: false,
                    breadcrumb: cleanedBreadcrumbs,
                    groupedDoorsNumber,
                });
            } else if (
                metric &&
                getterParams.node.data?.type === BULK_IDENTIFIER &&
                metric.kind === MetricsKind.AVERAGE_UNITS
            ) {
                valueGetter = metricsValueGetterCreator({
                    metric,
                    deliveryId: groupId,
                    doorId: door.id,
                    totalType,
                    doors: [door],
                    filteredDeliveriesForAssortmentSet,
                    filteredDoorsForAssortmentSet,
                    delimiter,
                    classificationHierarchy,
                    useSize: false,
                    rootGridApi,
                });
            } else {
                valueGetter = metricsValueGetterCreator({
                    metric,
                    deliveryId: groupId,
                    doorId: door.id,
                    totalType,
                    doors: [door],
                    filteredDeliveriesForAssortmentSet,
                    filteredDoorsForAssortmentSet,
                    delimiter,
                    classificationHierarchy,
                    useSize: shouldUseSizeParam(useSize, getterParams),
                    rootGridApi,
                });
            }

            if (typeof valueGetter === "string") {
                return valueGetter;
            }
            return valueGetter(getterParams);
        },
        valueSetter: valueSetterCreator({
            isTotalColumn: false,
            breadcrumb: cleanedBreadcrumbs,
            groupedDoorsNumber,
        }),
        valueFormatter: metric
            ? getMetricsValueFormatter({ kind: metric.kind, numberFormat: metric.numberFormat })
            : null,
        cellClass: mergedCellClass(
            (params) => getCellClassBasedOnColType(params),
            (params) => getCellClassBasedOnRowType(params),
            (params) => getCellClassForMetricBasedOnEditable(params, colId, totalType),
            cellClass
        ),
        cellStyle: ({ columnApi, colDef }) => {
            const column = columnApi.getColumn(colDef.colId);
            if (isFirstDoorColumnInGroup(column)) {
                return merge(true, cellClassToAlignRight, cellClassForLeftSeparator);
            }

            return cellClassToAlignRight;
        },
        ...(door?.doorNotes
            ? { headerTooltip: escapeHTML(door.doorNotes), headerComponent: "headerCompWithNote" }
            : {}),
    };
    if (classificationLabel) {
        colDef.headerComponentParams = {
            template: generateHeaderWithClassificationLabelTemplate(classificationLabel),
        };
    }
    return colDef;
};

interface IBuildTotalColumn {
    headerName: string;
    groupId?: string;
    classificationName?: string;
    classificationValue?: string;
    hierarchy: Array<string | number>;
    type: Omit<keyof ColumnTypes, ColumnTypes.DoorBulkQuantity>;
    cols: string[][];
    editable: ColDef["editable"];
    valueGetterCreator: IValueGetterCreator;
    valueSetterCreator: IValueSetterCreator;
    reviewPlan?: any;
    cellClass?: ColDef["cellClass"];
    doors?: IDoor[];
    merchantCurrency?: ICurrency;
    sortable?: boolean;
    hide?: boolean;
}

export const buildTotalColumn = ({
    headerName,
    groupId,
    classificationName,
    classificationValue,
    hierarchy,
    cols,
    valueGetterCreator,
    valueSetterCreator,
    type,
    editable = true,
    sortable = true,
    cellClass,
    doors,
    merchantCurrency,
    hide,
}: IBuildTotalColumn): ColDef => {
    const creatorParams = {
        breadcrumb: cols,
        isTotalColumn: true,
    };
    const colId = getColIdByColType(type as ColumnTypes, hierarchy);
    if (classificationValue === undefined) {
        // group total columns will have undefined. Note that "" is also valid for classificationValue
        // Ideally totalCols should not be global. See improvement ticket https://nuorder-inc.atlassian.net/browse/NU-23217
        totalCols = [...totalCols, ...cols];
    }

    return {
        headerClass: ({ column }) => {
            if (isFirstColumnInGroup(column, false)) {
                return "borderLeftClass";
            }

            return "";
        },
        headerName: formatMessage(headerName),
        colId,
        type: type as string,
        filter: false,
        sortable,
        lockVisible: true,
        cellRendererSelector: getCellRendererSelector(null, RowRenderers.CUSTOM_PINNED_ROW_RENDERER),
        // use getCellRendererParamsFn if you use more than one cell renderers
        cellRendererParams: {
            kind: Kinds.DOOR_BULK_QUANTITY,
            allocationReferenceFields: {
                //total value get currency symbol from merchantCurrency
                retailFieldName: undefined,
                wholesaleFieldName: undefined,
            },
            currency: merchantCurrency,
            groupId,
            classificationName,
            classificationValue,
            splitValue: type === ColumnTypes.AllocationDimensionTotal,
            doors,
            getTotalHelper: (planData, gridContext) => {
                return getTotal({
                    colType: type as
                        | ColumnTypes.AllocationDimensionTotal
                        | ColumnTypes.AllDimensionTotal
                        | ColumnTypes.ClassificationTotal,
                    groupId,
                    doorTotals: gridContext.doorTotals,
                    totalType: planData.totalType,
                    classificationName,
                    classificationValue,
                });
            },
            getPlanHelper: (planData, gridContext) => {
                return getPlan({
                    colType: type as
                        | ColumnTypes.AllocationDimensionTotal
                        | ColumnTypes.AllDimensionTotal
                        | ColumnTypes.ClassificationTotal,
                    groupId,
                    classificationName,
                    classificationValue,
                    reviewPlan: planData.plan,
                    doorTotals: gridContext.doorTotals,
                    dimensionFilters: gridContext.dimensionFilters,
                    doors: gridContext.doors,
                    totalType: planData.totalType,
                });
            },
        },
        pinnedRowValueFormatter: pinnedValueFormatter,
        valueFormatter: (params) => (params.value ? Number(params.value.toFixed(1)).toString() : undefined),
        width: 90,
        cellEditor: Editors.NumberCellEditor,
        cellEditorParams: {
            isZeroValueAllowed: true,
        },
        suppressColumnsToolPanel: true,
        suppressMenu: true,
        suppressMovable: true,
        editable: getPinnedRowEditable.bind(this, editable),
        cellClass: mergedCellClass(
            (params) => getCellClassForDoorsGroupTotalBasedOnEditable(params, colId, editable),
            cellClass
        ),
        valueGetter: valueGetterCreator(creatorParams),
        valueSetter: valueSetterCreator(creatorParams),
        cellStyle: ({ columnApi, colDef }) => {
            const column = columnApi.getColumn(colDef.colId);
            if (isFirstColumnInGroup(column, false)) {
                return merge(true, cellClassToAlignRight, cellClassForLeftSeparator);
            }

            return cellClassToAlignRight;
        },
        hide,
    };
};

export enum Order {
    Ascending = "asc",
    Descending = "desc",
    Custom = "custom",
}

interface IGetDoorsGroupColumnsOptions {
    // Root groups that will wrap all doors groups. Eg: deliveries months
    globalGroups?: IAllocationDimensionValue[];
    doors: IDoor[];
    doorClassificationSchema: ISchemaKey[];
    dimensionFilters?: string[][];
    attributeFilters?: IAttributeFilters;
    collapsedGroupIds?: string[];
    hiddenGroupIds?: string[];
    valueGetterCreator: IValueGetterCreator;
    valueSetterCreator: IValueSetterCreator;
    totalEditable?: ColDef["editable"];
    editable?: ColDef["editable"];
    groupDirection?: Order;
    reviewPlan?: any;
    delimiter?: string;
    cellClass?: ColDef["cellClass"];
    currentLayout: IPreference[];
    movable?: boolean;
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    merchantCurrency?: ICurrency;
    dimensionsPanelApplied: DimensionsPanelReduxState;
    gridParams: IGridParamsQueryParam;
    isServerSide?: boolean;
    assortmentDimensionMetrics?: boolean;
    allocationBagRef?: { allocationBag: AllocationBag };
    useSize?: boolean;
    isolatedMetric?: MetricsKind;
    rootGridApi?: GridApi; // Reference to main Grid api. Immediate use case: referencing main grid from CDP sizing grid.
}

const valueOrSanitized = (v) => (isNaN(v) ? v : v + ".0");

export const reconcileTrees = (abstractTree: IPreference[], doorColumns: ColDef[]) => {
    if (!abstractTree || !abstractTree.length) {
        return doorColumns;
    }

    function reconcileLeft(abstractTree: IPreference[], colTree) {
        return abstractTree.reduce((acc, node) => {
            const columnNode = colTree[valueOrSanitized(node.field_name)];
            if (columnNode) {
                acc.push({
                    ...columnNode._col,
                    width: node.grid_column.width,
                    ...(node.children ? { children: reconcileLeft(node.children, columnNode) } : {}),
                });
            }
            return acc;
        }, []);
    }

    function reconcileRight(partialTree, colTree) {
        const leftColKeys = Object.keys(partialTree);
        const rightColKeys = Object.keys(colTree);
        const doesLeftColTreeContainMetrics = leftColKeys.find((key) => getMetricColIdSuffix(key) != undefined);
        const doesRightColTreeContainMetrics = rightColKeys.find((key) => getMetricColIdSuffix(key) != undefined);
        const doesLeftColTreeContainDoorAllocation = leftColKeys.find(
            (key) =>
                key.includes(ColumnTypes.AllocationDimensionTotal) ||
                key.includes(ColumnTypes.ClassificationTotal) ||
                key.includes(ColumnTypes.DoorBulkQuantity)
        );
        const doesRightColTreeContainDoorAllocation = rightColKeys.find(
            (key) =>
                key.includes(ColumnTypes.AllocationDimensionTotal) ||
                key.includes(ColumnTypes.ClassificationTotal) ||
                key.includes(ColumnTypes.DoorBulkQuantity)
        );
        let combinedKeys;
        // Adding a special case here to use right keys ordering for metric and door unit columns with the incoming changes
        if (
            (doesRightColTreeContainMetrics && doesLeftColTreeContainMetrics) ||
            (doesLeftColTreeContainDoorAllocation && doesRightColTreeContainDoorAllocation)
        ) {
            const orderedRightKeys = new globalThis.Set(rightColKeys);
            const leftKeys = leftColKeys.reduce((A, k) => {
                if (!orderedRightKeys.has(k)) {
                    A.push(k);
                }
                return A;
            }, []);
            combinedKeys = [...orderedRightKeys].concat(leftKeys);
        } else {
            const orderedLeftKeys = new globalThis.Set(leftColKeys);
            const rightKeys = Object.keys(colTree).reduce((A, k) => {
                if (!orderedLeftKeys.has(k)) {
                    A.push(k);
                }
                return A;
            }, []);
            combinedKeys = [...orderedLeftKeys].concat(rightKeys);
        }

        return combinedKeys.reduce((acc, nodeName) => {
            if (nodeName == "_col") {
                return acc;
            }
            const leftNode = partialTree[nodeName];
            if (!leftNode) {
                acc.push(colTree[nodeName]._col);
            } else {
                if (colTree[nodeName]._col.children && colTree[nodeName]._col.children.length > 0) {
                    acc.push({
                        ...leftNode._col,
                        children: reconcileRight(leftNode, colTree[nodeName]),
                    });
                } else {
                    acc.push({ ...leftNode._col });
                }
            }
            return acc;
        }, []);
    }

    const rightTree = buildNodeTree(doorColumns);
    const reconciledLeft = reconcileLeft(abstractTree, rightTree);
    const leftTree = buildNodeTree(reconciledLeft);
    let reconciledRight = reconcileRight(leftTree, rightTree);
    const totalColumn = reconciledRight.find((column) => column.headerName === formatMessage("Total"));

    if (totalColumn) {
        reconciledRight = reconciledRight.filter((column) => column.headerName !== formatMessage("Total"));
        reconciledRight.unshift(totalColumn);
    }
    return reconciledRight;
};

const buildNodeTree = (top: ColDef | ColGroupDef | (ColDef | ColGroupDef)[]) => {
    if (Array.isArray(top)) {
        return buildNodeTree({ groupId: "root", children: top });
    }
    if (!(top as ColGroupDef).children) {
        return { _col: top };
    }
    return (top as ColGroupDef).children.reduce((acc, node: any) => {
        acc[valueOrSanitized(node.groupId ?? node.colId)] = buildNodeTree(node);
        acc[valueOrSanitized(node.groupId ?? node.colId)]._col = node;
        return acc;
    }, {});
};

export const shouldHideDoorWithParams = (door: IDoor, shownDoors?: IDoor[]): boolean => {
    let hideDoorColumn = false;
    if (shownDoors) {
        hideDoorColumn =
            lodashFind(shownDoors, (d) => {
                return d.id === door.id;
            }) === undefined;
    }
    return hideDoorColumn;
};

interface IAllowedValues {
    id: string;
    label: string;
}
interface IDoorClassification {
    allowedValues: IAllowedValues[];
    schema: ISchemaKey;
}

export const buildDoorTree = (
    options: IGetDoorsGroupColumnsOptions,
    globalGroup: IAllocationDimensionValue,
    doors: IDoor[],
    classifications: IDoorClassification[],
    level: number = 0,
    shownDoors?: IDoor[],
    totalType?: MetricsTotalType,
    classificationHierarchy: IClasificationHierarchy[] = [],
    filteredDeliveriesForAssortmentSet?: Set<string>,
    filteredDoorsForAssortmentSet?: Map<string, IDoor>
): [(ColDef | ColGroupDef)[], string[][], number, string[][], string[][]] => {
    const {
        doorClassificationSchema,
        collapsedGroupIds = [],
        valueGetterCreator,
        valueSetterCreator,
        totalEditable,
        editable = false,
        hiddenGroupIds = [],
        delimiter = GROUP_ID_DELIMITER,
        dimensionFilters,
        attributeFilters,
        cellClass,
        movable = true,
        merchantPurchaseOrderDoorGroups,
        merchantCurrency,
        dimensionsPanelApplied,
        assortmentDimensionMetrics,
        allocationBagRef,
        useSize,
        rootGridApi,
        isolatedMetric,
        currentLayout,
    } = options;

    let totalDoorCount = 0;
    const metrics = dimensionsPanelApplied.metrics.values;
    const classificationLabelField = dimensionsPanelApplied.attributes.find(
        (attribute) => attribute.showLabelInHeader
    )?.id;
    const anyDoorsSelected = isAnyDoorSelected(dimensionsPanelApplied.dimensions);

    // Render doors
    const columns: (ColDef | ColGroupDef)[] = [];
    let includedBreadcrumbs: string[][] = [];
    if (level === classifications.length) {
        const activeDoorDisplayLabelProperty = doorClassificationSchema.find(
            (doorsDisplayProperty) => doorsDisplayProperty.field_name === classificationLabelField
        );
        for (const door of doors) {
            // Skip hidden door or filtered doors
            // or door/delivery filter active and door/date not in filter
            if (
                isDoorInClassification(door, {
                    activeClassification: null,
                    hiddenClassificationValues: [],
                    attributeFilters,
                }) &&
                isDimensionInFilters(dimensionFilters, [...(globalGroup.id ? [globalGroup.id] : []), door.id]) // and door-dimension in filters
            ) {
                const classificationValue = activeDoorDisplayLabelProperty
                    ? door.classifications[activeDoorDisplayLabelProperty.field_name]
                    : undefined;
                if (assortmentDimensionMetrics) {
                    /**
                     * Metrics Door Level
                     * Delivery | Attribute | Door | Value Col
                     * 0          0           0      Not possible, delivery is required
                     * >=1        0           0      Delivery
                     * >=1        0           1      Door
                     * >=1        1           1      Door
                     * >=1        1           0      Attribute 1
                     * >=1        2           0      Attribute 2
                     */
                    metrics
                        .filter((m) => (globalGroup.id === null ? m.showTotals : m.showValue))
                        .forEach((m) => {
                            const shouldHideDoor =
                                !m.showValue ||
                                !anyDoorsSelected ||
                                shouldHideDoorWithParams(door, shownDoors) ||
                                (isolatedMetric && m.kind !== isolatedMetric);
                            if (!shouldHideDoor) {
                                const metricCol = buildDoorColumn({
                                    headerName: [door.label, m.displayName].join(" "),
                                    breadcrumbs: [...(globalGroup.id ? [globalGroup.id] : []), door.id],
                                    groupId: globalGroup.id,
                                    doorId: door.id,
                                    metric: m,
                                    valueGetterCreator,
                                    valueSetterCreator,
                                    classificationLabel: getClassificationLabelBySchema(
                                        classificationValue,
                                        activeDoorDisplayLabelProperty,
                                        {
                                            merchantPurchaseOrderDoorGroups,
                                        }
                                    ),
                                    editable: (params) => isMetricEditable(params, m),
                                    cellClass,
                                    movable,
                                    door,
                                    hide: shouldHideDoor,
                                    sortable: !options.isServerSide,
                                    totalType,
                                    filteredDeliveriesForAssortmentSet,
                                    filteredDoorsForAssortmentSet,
                                    delimiter,
                                    classificationHierarchy,
                                    useSize,
                                    rootGridApi,
                                    currentLayout: options.currentLayout,
                                });
                                columns.push(metricCol);
                            }
                            includedBreadcrumbs.push([...(globalGroup.id ? [globalGroup.id] : []), door.id]);
                        });
                    totalDoorCount++;
                } else {
                    const doorColumn = buildDoorColumn({
                        headerName: door.label,
                        breadcrumbs: [...(globalGroup.id ? [globalGroup.id] : []), door.id],
                        groupId: globalGroup.id,
                        doorId: door.id,
                        valueGetterCreator,
                        valueSetterCreator,
                        classificationLabel: getClassificationLabelBySchema(
                            classificationValue,
                            activeDoorDisplayLabelProperty,
                            {
                                merchantPurchaseOrderDoorGroups,
                            }
                        ),
                        editable,
                        cellClass,
                        movable,
                        door,
                        hide: shouldHideDoorWithParams(door, shownDoors),
                        sortable: !options.isServerSide,
                        totalType,
                        filteredDeliveriesForAssortmentSet,
                        filteredDoorsForAssortmentSet,
                        classificationHierarchy,
                        rootGridApi,
                        currentLayout: options.currentLayout,
                    });
                    includedBreadcrumbs.push([...(globalGroup.id ? [globalGroup.id] : []), door.id]);
                    columns.push(doorColumn);
                    totalDoorCount++;
                }
            }
        }
    } else {
        // it is a classification
        const currentClassification = classifications[level];
        const collectionId = currentClassification.schema.collectionId;
        currentClassification.allowedValues.forEach((classification, i) => {
            const attributeDoors = doors.filter(
                (door) =>
                    // Dimension values (Door) within this attribute/classification (Assortment).
                    isDoorInClassification(door, {
                        activeClassification: currentClassification.schema.field_name,
                        hiddenClassificationValues: hiddenGroupIds,
                        collectionId,
                        attributeFilters,
                    }) &&
                    // Dimension values (Door) within this attribute/classification (Rollup).
                    (door.classifications[
                        collectionId
                            ? `${currentClassification.schema.field_name}${ROLLUP_ALLOCATION_DIMENSION_DELIMITER}${collectionId}`
                            : currentClassification.schema.field_name
                    ] ?? "") === classification.id
            );

            const [children, breadcrumbs, displayedDoors, _displayedDoorCols, uniqueTotals] = buildDoorTree(
                options,
                globalGroup,
                attributeDoors,
                classifications,
                level + 1,
                shownDoors,
                totalType,
                classificationHierarchy.concat([
                    {
                        field: currentClassification.schema.field_name,
                        value: classification.id || "",
                    },
                ]),
                filteredDeliveriesForAssortmentSet,
                filteredDoorsForAssortmentSet
            );
            // Attributes with zero doors should not be displayed on Grid
            if (displayedDoors === 0) {
                return;
            }
            totalDoorCount = totalDoorCount + displayedDoors;
            includedBreadcrumbs = includedBreadcrumbs.concat(breadcrumbs);
            const groupId = getGroupId(delimiter, globalGroup.id, classification.id);

            let metricsCols = [];
            if (assortmentDimensionMetrics) {
                /**
                 * Metrics Attributes Level
                 *
                 * Delivery | Attribute | Door | Subtotal Col
                 * 0          0           0      Not possible, delivery is required
                 * >=1        0           0      None (Value col is for delivery level arbitrarily)
                 * >=1        0           1      Delivery
                 * >=1        1           1      Delivery, Attribute 1
                 * >=1        1           0      Delivery
                 * >=1        2           0      Delivery, Attribute 1 (Attribute 2 is Value col)
                 *
                 * Dimensions: Deliveries and Doors
                 * Attributes: Climate, Channel. Each attribute has a selection of values, e.g. Climate: Warm, Cold, Blank
                 *
                 * Columns are grouped in a hierarchy of levels, where:
                 * Deliveries > Attribute 1 > Attribute 2 > ... > Doors
                 *
                 * Delivery level is always selected. Other levels can be selected or deselected.
                 *
                 * Metrics have kinds, e.g. Units, Average Units, Cost, etc
                 * Metrics also have 3 toggle types, i.e. Total, Subtotal, Value
                 *
                 * The toggle type control which levels of columns to show. E.g.
                 * - Value Toggle control the last selected level:
                 *   Case 1: Value Toggle controls Delivery level
                 *   Case 2: Value Toggle controls Attribute 1 level, ...
                 *
                 * - Subtotal Toggle control all other levels up to Delivery
                 *   Case 1: Subtotal Toggle controls nothing
                 *   Case 2: Subtotal Toggle controls Delivery level
                 */

                /* Dimension/Attribute Level mapped to Metric Toggle Type
                | * selected
                | Case 1 ---------------- |
                | Delivery | * | value    |
                | Attr   1 |   |          |
                | Attr   2 |   |          |
                | Door     |   |          |
                | Case 2 ---------------- |
                | Delivery | * | subtotal |
                | Attr   1 | * | value    |
                | Attr   2 |   |          |
                | Door     |   |          |
                | Case 3 ---------------- |
                | Delivery | * | subtotal |
                | Attr   1 | * | subtotal |
                | Attr   2 | * | value    |
                | Door     |   |          |
                | Case 4 ---------------- |
                | Delivery | * | subtotal |
                | Attr   1 |   |          |
                | Attr   2 |   |          |
                | Door     | * | value    |
                | Case 5 ---------------- |
                | Delivery | * | subtotal |
                | Attr   1 | * | subtotal |
                | Attr   2 |   |          |
                | Door     | * | value    |
                | Case 6 ---------------- |
                | Delivery | * | subtotal |
                | Attr   1 | * | subtotal |
                | Attr   2 | * | subtotal |
                | Door     | * | value    |
                | ----------------------- |
                */
                metricsCols = metrics.reduce((acc, metric) => {
                    let hide = true;
                    let valueSetter;
                    let metricColumnEditable = false;
                    if (isEmpty(classifications)) {
                        hide = true;
                    } else if (isolatedMetric) {
                        hide = metric.kind !== isolatedMetric;
                    } else if (globalGroup.id === null) {
                        hide = !metric.showTotals;
                    } else if (anyDoorsSelected) {
                        hide = !metric.showSubtotals;
                    } else if (level < classifications.length - 1) {
                        hide = !metric.showSubtotals;
                    } else {
                        hide = !metric.showValue;
                    }

                    if (hide) {
                        return acc;
                    }

                    if (
                        [MetricsKind.AVERAGE_UNITS, MetricsKind.UNITS].includes(metric.kind as any) &&
                        breadcrumbs.length
                    ) {
                        metricColumnEditable = true;
                        const dataEntryType =
                            (metric.kind as any) === MetricsKind.AVERAGE_UNITS
                                ? UnitsEntryType.GroupAverage
                                : UnitsEntryType.Total;
                        valueSetter = defaultColumnDefsValueSetterCreator(dataEntryType, Boolean(allocationBagRef), {
                            breadcrumb: uniqueTotals,
                            isTotalColumn: true,
                            attributeDoors,
                        });
                    }
                    if (totalType === MetricsTotalType.GrandTotalMetric) {
                        metricColumnEditable = false;
                    }
                    const displayName = metric.displayName;
                    const colId = generateMetricColId(groupId, metric.kind);

                    const savedWidth = getSavedWidth(colId, currentLayout);

                    const headerName = [classification.label || "Blank", displayName].join(" ");
                    acc.push({
                        headerName,
                        headerClass: ({ column }) => {
                            if (isFirstColumnInGroup(column, false)) {
                                return "borderLeftClass";
                            }

                            return "";
                        },
                        colId: generateMetricColId(groupId, metric.kind),
                        width: savedWidth ?? getAgGridColumnWidth({ text: headerName }),
                        hide,
                        editable: (params: EditableCallbackParams) =>
                            isMetricCellEditable(params, metricColumnEditable),
                        lockVisible: true,
                        suppressMenu: true,
                        suppressColumnsToolPanel: true,
                        suppressMovable: true,
                        sortable: !options.isServerSide,
                        valueGetter: (getterParams: ValueGetterParams) => {
                            // Add case for CDP bulk row to use passed in value getter creator for units and avg units metric columns
                            const valueGetter = metricsValueGetterCreator({
                                metric,
                                deliveryId: globalGroup.id,
                                doors: attributeDoors,
                                doorGroup: currentClassification.schema.collectionId
                                    ? getUserDefinedClassificationName(
                                          currentClassification.schema.field_name,
                                          currentClassification.schema.collectionId
                                      )
                                    : currentClassification.schema.field_name,
                                classification: classification.id,
                                classificationHierarchy,
                                totalType,
                                filteredDeliveriesForAssortmentSet,
                                filteredDoorsForAssortmentSet,
                                delimiter,
                                useSize: shouldUseSizeParam(useSize, getterParams),
                                rootGridApi,
                            });

                            if (typeof valueGetter === "string") {
                                return valueGetter;
                            }
                            return valueGetter(getterParams);
                        },
                        valueSetter,
                        type: ColumnTypes.ClassificationTotal,
                        valueFormatter: getMetricsValueFormatter({
                            kind: metric.kind,
                            numberFormat: metric.numberFormat,
                        }),
                        cellRendererSelector: getCellRendererSelector(null, RowRenderers.CUSTOM_PINNED_ROW_RENDERER),
                        cellClass: mergedCellClass(
                            (params) => getCellClassBasedOnColType(params),
                            (params) => getCellClassBasedOnRowType(params),
                            (params) => getCellClassBasedOnEditable(params, colId),
                            cellClass
                        ),
                        cellRendererParams: {
                            metric,
                            splitValue: true,
                            dimensionIds: globalGroup
                                ? [
                                      globalGroup.id === null
                                          ? getFilteredDeliveriesArray(filteredDeliveriesForAssortmentSet)
                                          : [globalGroup.id],
                                      attributeDoors.map((door) => door.id),
                                  ]
                                : [attributeDoors.map((door) => door.id)],
                        },
                        cellStyle: ({ columnApi, colDef }) => {
                            const column = columnApi.getColumn(colDef.colId);
                            if (isFirstColumnInGroup(column, false)) {
                                return cellClassForLeftSeparator;
                            }

                            return {};
                        },
                        cellEditor: Editors.NumberCellEditor,
                        cellEditorParams: {
                            isZeroValueAllowed: true,
                        },
                    });

                    return acc;
                }, []);
            }

            const columnChildren = [
                // We only render non-metrics total when metrics feature is off
                ...(!assortmentDimensionMetrics
                    ? [
                          buildTotalColumn({
                              headerName: formatMessage("|headerName| Total", {
                                  headerName: classification.label || formatMessage("Blank"),
                              }),
                              groupId: globalGroup.id,
                              classificationName: currentClassification.schema?.field_name,
                              classificationValue: classification.id,
                              hierarchy: [globalGroup.id, i], // Not sure this is needed
                              cols: breadcrumbs,
                              editable: totalEditable,
                              valueGetterCreator,
                              valueSetterCreator,
                              type: ColumnTypes.ClassificationTotal,
                              cellClass,
                              doors,
                              merchantCurrency,
                              sortable: !options.isServerSide,
                              hide: assortmentDimensionMetrics,
                          }),
                      ]
                    : []),
                ...metricsCols,
            ];
            const colDef = {
                openByDefault: !collapsedGroupIds.includes(groupId),
                columnGroupShow: "open",
                headerName: `${classification.label || formatMessage("Blank")} (${displayedDoors})`,
                headerClass: "borderLeftClass",
                groupId,
                lockVisible: true,
                marryChildren: true,
                suppressColumnsToolPanel: true,
                children: columnChildren.concat(children),
            };
            columns.push(colDef);
        });
    }
    // find displayed door columns.
    const unhiddenDoorColIds = columns
        .filter((column: ColDef) => !column.hide)
        .map((column: ColDef) => column.cellRendererParams?.doorId);
    const displayedDoorCols = includedBreadcrumbs.filter((breadcrumb) =>
        unhiddenDoorColIds.includes(breadcrumb[breadcrumb.length - 1])
    );
    const uniqueTotals = lodashUniqWith(includedBreadcrumbs, lodashIsEqual);
    return [columns, includedBreadcrumbs, totalDoorCount, displayedDoorCols, uniqueTotals];
};

export const isAllocationDimensionDisplayed = (dimensionView: Dimension | undefined) => {
    // Always interpret undefined as include all
    return (value: string) =>
        !dimensionView?.values ||
        (dimensionView?.values ?? []).some(
            (dimensionViewValue) => dimensionViewValue.id === value && dimensionViewValue.selected
        );
};

export const sortDatesOrDoors = (
    items: IDoor[] | IAllocationDimensionValue[],
    direction: Order,
    customOrder?: string[]
) => {
    let sortedItems = [...items];
    if (direction === Order.Ascending) {
        // Keep the default order unchanged
    } else if (direction === Order.Descending) {
        sortedItems.reverse();
    } else if (direction === Order.Custom && customOrder) {
        sortedItems = sortedItems.sort(
            (valueA, valueB) => customOrder.indexOf(valueA.id) - customOrder.indexOf(valueB.id)
        );
    }

    return sortedItems;
};

export const filterDatesOrDoors = (
    items: IDoor[] | IAllocationDimensionValue[],
    type: "dates" | "doors",
    dimensionFilters: string[][],
    hasGlobalGroup: boolean,
    attributeFilters?: IAttributeFilters
) => {
    let dimensionFilterIndex;
    if (type === "dates") {
        dimensionFilterIndex = 0;
    } else if (type === "doors") {
        // If there is global group (eg: delivery), door will be 2nd inde
        dimensionFilterIndex = hasGlobalGroup ? 1 : 0;
    }

    return (items as unknown as any).filter((item: IDoor | IAllocationDimensionValue) => {
        const filterPair = Array(hasGlobalGroup ? 2 : 1).fill(null);
        filterPair[dimensionFilterIndex] = item.id;
        return (
            isDimensionInFilters(dimensionFilters, filterPair) &&
            (type !== "doors" ||
                isDoorInClassification(item as IDoor, {
                    activeClassification: null,
                    hiddenClassificationValues: [],
                    attributeFilters,
                }))
        );
    });
};

export const getDoorAttributeWithAllowedValuesWithBlankValue = ({
    allocationDimensionAttribute,
    blankAtBeginning,
}: {
    allocationDimensionAttribute: ISchemaKey;
    blankAtBeginning?: boolean;
}) => {
    const allowedValues = allocationDimensionAttribute.constraint?.allowed_values ?? [];
    // Inserting a blank value if it's not present inside allowed_values
    const allowedValuesWithNoBlankValue = allowedValues.filter(({ string_value }) => string_value !== "");
    return {
        ...allocationDimensionAttribute,
        constraint: {
            allowed_values: blankAtBeginning
                ? [{ string_value: "" }, ...allowedValuesWithNoBlankValue]
                : [...allowedValuesWithNoBlankValue, { string_value: "" }],
        },
    };
};

export const getDoorsGroupColumns = (options: IGetDoorsGroupColumnsOptions): Array<ColGroupDef | ColDef> => {
    const {
        doors,
        doorClassificationSchema,
        collapsedGroupIds = [],
        valueGetterCreator,
        valueSetterCreator,
        totalEditable,
        cellClass,
        currentLayout = [],
        merchantPurchaseOrderDoorGroups,
        merchantCurrency,
        groupDirection,
        assortmentDimensionMetrics,
        allocationBagRef,
        dimensionFilters,
        dimensionsPanelApplied,
        attributeFilters,
        useSize,
        rootGridApi,
        isolatedMetric,
    } = options;

    // Global group is the highest dimension. Eg: Delivery
    const hasGlobalGroup = options.globalGroups?.length > 0;
    // Default globalGroups to array containing empty group so we can run below logic just once.
    let globalGroups: IAllocationDimensionValue[] = options.globalGroups?.length
        ? options.globalGroups
        : [
              {
                  id: "",
                  external_id: "",
                  name: "",
              } as IAllocationDimensionValue,
          ];

    // Respect dimension filters to filter out dimension values
    globalGroups = filterDatesOrDoors(globalGroups, "dates", dimensionFilters, options.globalGroups?.length > 0);
    const filteredDoors = filterDatesOrDoors(
        doors,
        "doors",
        dimensionFilters,
        options.globalGroups?.length > 0,
        attributeFilters
    );

    const allocationDimensionViews = dimensionsPanelApplied.dimensions;
    const highestAllocationDimensionView = allocationDimensionViews?.[hasGlobalGroup ? 0 : -1];
    const lowestAllocationDimensionView = allocationDimensionViews?.[(allocationDimensionViews?.length ?? 0) - 1];

    const isDeliveryDimensionDisplayed = isAllocationDimensionDisplayed(highestAllocationDimensionView);
    const isDoorDimensionDisplayed = isAllocationDimensionDisplayed(lowestAllocationDimensionView);

    globalGroups = sortDatesOrDoors(
        globalGroups.filter((group) => isDeliveryDimensionDisplayed(group.id)),
        highestAllocationDimensionView?.sort,
        highestAllocationDimensionView?.values.map((dimensionViewValue) => dimensionViewValue.id)
    ) as IAllocationDimensionValue[];

    // This is list of VISIBLE doors. This only matters for presentation purpose. Hidden door will still be included in calculation.
    const shownDoors: IDoor[] = filteredDoors.filter((door) => isDoorDimensionDisplayed(door.id));

    // This is list of ALL doors. We need complete list of doors for correct calculation. We want to count hidden doors.
    const sortedDoors = sortDatesOrDoors(
        filteredDoors,
        lowestAllocationDimensionView?.sort,
        lowestAllocationDimensionView?.values.map((dimensionViewValue) => dimensionViewValue.id)
    ) as IDoor[];

    const activeAttributes: Attribute[] = dimensionsPanelApplied.attributes.filter((attribute) => attribute.selected);
    const classifications: IDoorClassification[] = [];

    // Generate delivery and door sets to optimize performance for horizontal formulas
    const filteredDeliveriesForAssortmentSet = new Set<string>(globalGroups.map((dimensionValue) => dimensionValue.id));
    const filteredDoorsForAssortmentSet = new Map<string, IDoor>(filteredDoors.map((door) => [door.id, door]));

    // Reseting the cache for isDoorVisible to avoid stale data when mapping
    // classification columns
    isAnyDoorVisible.cache.clear();
    for (const activeAttribute of activeAttributes) {
        const classificationCollectionId = activeAttribute.id;
        const direction = activeAttribute.sort || groupDirection;
        const [classification, collectionId] =
            classificationCollectionId.split(SR_CUSTOM_DOOR_GROUPING_DELIMITER) || [];
        const currentDoorAttribute = doorClassificationSchema.find(
            (doorsDisplayProperty) =>
                doorsDisplayProperty.field_name === classification &&
                (!collectionId || doorsDisplayProperty.collectionId === collectionId)
        );

        if (!currentDoorAttribute) {
            continue;
        }
        const updatedDoorAttribute = getDoorAttributeWithAllowedValuesWithBlankValue({
            allocationDimensionAttribute: currentDoorAttribute,
        });
        let blankAllowedValue: IAllowedValues;
        let allowedValues: IAllowedValues[];

        const attributeView = activeAttributes.find(
            (attribute) => attribute.id === (collectionId ? classificationCollectionId : classification)
        );

        if (direction === Order.Custom && attributeView?.values) {
            // TODO: JOSE. Add UT
            allowedValues = (attributeView?.values ?? [])
                .filter((attributeValue) => attributeValue.selected)
                .map((attributeValue) => ({
                    id: attributeValue.id,
                    label: attributeValue.label,
                }));
        } else {
            allowedValues = lodashGet(updatedDoorAttribute, "constraint.allowed_values", [])
                .map((schemaAllowedValue) => ({
                    id: schemaAllowedValue.string_value,
                    label: schemaAllowedValue.string_value,
                }))
                .filter((schemaAllowedValue) => {
                    if (!schemaAllowedValue.id) {
                        blankAllowedValue = schemaAllowedValue;
                        return false;
                    }
                    return true;
                });

            // PO Door Group gets its options from `merchantPurchaseOrderDoorGroups` instead of from schema.
            if (
                updatedDoorAttribute?.field_name === PURCHASE_ORDER_DOOR_GROUP_FIELD &&
                merchantPurchaseOrderDoorGroups.length > 0
            ) {
                allowedValues = merchantPurchaseOrderDoorGroups.map((merchantPurchaseOrderDoorGroup) => ({
                    id: merchantPurchaseOrderDoorGroup.id,
                    label: merchantPurchaseOrderDoorGroup.name,
                }));
            }

            // Combine used values with allowed_values
            if (updatedDoorAttribute?.field_name) {
                doors.forEach((door) => {
                    const doorClassification = door.classifications[updatedDoorAttribute?.field_name];
                    if (
                        doorClassification &&
                        !allowedValues.some((allowedValue) => allowedValue.id === doorClassification) &&
                        (!collectionId ||
                            updatedDoorAttribute?.constraint?.allowed_values?.some(
                                (it) => it.string_value === doorClassification
                            ))
                    ) {
                        allowedValues.push({
                            id: doorClassification.toString(),
                            label: doorClassification.toString(),
                        });
                    }
                });
            }
            if (direction === Order.Ascending) {
                // Keep `allowedValues` order unchanged when it's ascending
            } else if (direction === Order.Descending) {
                allowedValues.reverse();
            }

            // Always adds [Blank] to the last
            if (blankAllowedValue) {
                allowedValues.push(blankAllowedValue);
            }

            allowedValues = allowedValues.filter((schemaAllowedValue) => {
                // Filter out value that is not listed in `attributeValue.values`
                if (attributeView && attributeView.values) {
                    return (
                        attributeView.values.some(
                            (attributeValue) => attributeValue.id === schemaAllowedValue.id && attributeValue.selected
                        ) &&
                        (!attributeFilters?.[classification]?.length ||
                            attributeFilters[classification].includes(schemaAllowedValue.label))
                    );
                }

                if (
                    attributeFilters?.[classification]?.length &&
                    !attributeFilters[classification].includes(schemaAllowedValue.label)
                ) {
                    return false;
                }
                return true;
            });
        }

        classifications.push({
            allowedValues,
            schema: updatedDoorAttribute,
        });
    }

    let colDefs = [];
    totalCols = [];
    const metrics = dimensionsPanelApplied.metrics.values;
    const anyDoorsSelected = isAnyDoorSelected(dimensionsPanelApplied.dimensions);

    globalGroups.forEach((globalGroup, index1) => {
        const [childrenColDefs, allDoorColumns, totalDoorCount, _displayedDoorCols, uniqueTotals] = buildDoorTree(
            options,
            globalGroup,
            sortedDoors,
            classifications,
            0,
            shownDoors,
            MetricsTotalType.TotalMetric,
            [],
            filteredDeliveriesForAssortmentSet,
            filteredDoorsForAssortmentSet
        );
        // add children to parent grouping (eg: delivery date)
        if (childrenColDefs.length > 0 || metrics.some((m) => m.showSubtotals || m.showValue)) {
            const groupId = getGroupId(getGroupId, globalGroup.id);
            // If there is no active grouping, this column should be editable, except if overridden by `totalEditable`
            const isParentTotalEditable =
                classifications.length === 0 ? (totalEditable !== undefined ? totalEditable : true) : false;

            let metricsCols = [];
            if (assortmentDimensionMetrics) {
                /**
                 * Metrics Delivery Level
                 */
                metricsCols = metrics.reduce<ColDef[]>((acc, metric) => {
                    let hide;
                    let valueSetter;
                    let editable = false;
                    if (isolatedMetric) {
                        hide = metric.kind !== isolatedMetric;
                    } else if (!anyDoorsSelected && classifications.length === 0) {
                        hide = !metric.showValue;
                    } else {
                        hide = !metric.showSubtotals;
                    }

                    if (hide) {
                        return acc;
                    }

                    if (
                        [MetricsKind.AVERAGE_UNITS, MetricsKind.UNITS].includes(metric.kind as any) &&
                        allDoorColumns.length
                    ) {
                        if (!activeAttributes.length) {
                            editable = true;
                        }
                        valueSetter = defaultColumnDefsValueSetterCreator(
                            getEntryType(metric),
                            Boolean(allocationBagRef),
                            {
                                breadcrumb: uniqueTotals,
                                isTotalColumn: true,
                            }
                        );
                    }

                    const colId = generateMetricColId(groupId, metric.kind);

                    const savedWidth = getSavedWidth(colId, currentLayout);

                    const headerName = [globalGroup.name, metric.displayName].join(" ");
                    acc.push({
                        headerName,
                        headerClass: ({ column }) => {
                            const groupColumnGroup = (column as any).columnApi.getColumnGroup(groupId);
                            if (isFirstColumnInGroup(column, true, groupColumnGroup)) {
                                return "borderLeftClass";
                            }

                            return "";
                        },
                        colId: colId,
                        hide,
                        type: ColumnTypes.AllocationDimensionTotal,
                        width: savedWidth ?? getAgGridColumnWidth({ text: headerName }),
                        sortable: !options.isServerSide,
                        suppressMovable: true,
                        cellRendererSelector: getCellRendererSelector(null, RowRenderers.CUSTOM_PINNED_ROW_RENDERER),
                        valueGetter: (getterParams: ValueGetterParams) => {
                            // Add case for CDP bulk row to use passed in value getter creator for units and avg units metric columns
                            const valueGetter = metricsValueGetterCreator({
                                metric,
                                deliveryId: groupId,
                                totalType: MetricsTotalType.TotalMetric,
                                doors: sortedDoors,
                                filteredDeliveriesForAssortmentSet,
                                filteredDoorsForAssortmentSet,
                                useSize: shouldUseSizeParam(useSize, getterParams),
                                rootGridApi,
                            });

                            if (typeof valueGetter === "string") {
                                return valueGetter;
                            }
                            return valueGetter(getterParams);
                        },
                        valueFormatter: getMetricsValueFormatter({
                            kind: metric.kind,
                            numberFormat: metric.numberFormat,
                        }),
                        editable: (params: EditableCallbackParams) => isMetricCellEditable(params, editable),
                        valueSetter,
                        cellClass: mergedCellClass(
                            (params) => getCellClassBasedOnColType(params),
                            (params) => getCellClassBasedOnRowType(params),
                            (params) => getCellClassBasedOnEditable(params, colId),
                            cellClass
                        ),
                        cellRendererParams: {
                            metric,
                            dimensionIds: groupId
                                ? [[groupId], sortedDoors.map((door) => door.id)]
                                : [sortedDoors.map((door) => door.id)],
                            splitValue: true,
                        },
                        cellStyle: ({ columnApi, colDef }) => {
                            const column = columnApi.getColumn(colDef.colId);
                            const groupColumnGroup = columnApi.getColumnGroup(groupId);
                            if (isFirstColumnInGroup(column, true, groupColumnGroup)) {
                                return cellClassForLeftSeparator;
                            }

                            return {};
                        },
                    });

                    return acc;
                }, []);
            }

            colDefs = colDefs.concat(
                globalGroup.id
                    ? [
                          {
                              openByDefault: !collapsedGroupIds.includes(groupId),
                              headerName: `${globalGroup.name} (${totalDoorCount})`,
                              groupId,
                              suppressColumnsToolPanel: true,
                              children: [
                                  // We only render non-metrics total when metrics feature is off
                                  ...(!assortmentDimensionMetrics
                                      ? [
                                            buildTotalColumn({
                                                headerName: formatMessage("|headerName| Total", {
                                                    headerName: globalGroup.name,
                                                }),
                                                groupId: globalGroup.id,
                                                hierarchy: [index1],
                                                cols: allDoorColumns,
                                                editable: isParentTotalEditable,
                                                valueGetterCreator,
                                                valueSetterCreator,
                                                type: ColumnTypes.AllocationDimensionTotal,
                                                cellClass,
                                                doors: sortedDoors,
                                                merchantCurrency,
                                                hide: assortmentDimensionMetrics,
                                            }),
                                        ]
                                      : []),
                                  ...metricsCols,
                                  ...childrenColDefs,
                              ],
                              headerGroupComponent: "headerCompWithMenu",
                              headerClass: "borderLeftClass",
                              marryChildren: true,
                              suppressMovable: true,
                          } as ColGroupDef,
                      ]
                    : childrenColDefs
            );
        }
    });
    const creatorParams = {
        breadcrumb: [...totalCols],
        isTotalColumn: true,
    };

    // global total column. For merchant with metrics, it will be the individual total metrics.
    let grandTotalCols: ColDef[];
    // The individual door columns under Total group.
    // this will be generated from doors when a merchant does not have metrics, otherwise from metrics.
    let doorTotalColumns: (ColDef | ColGroupDef)[];

    if (assortmentDimensionMetrics) {
        /**
         * Metrics Total Columns
         * - Metrics toggle is independent by type
         * - Metric Total cols replace the overall Total col in the leftmost group
         */
        grandTotalCols = metrics.reduce((acc, metric) => {
            const hide = !metric.showTotals || (isolatedMetric && metric.kind !== isolatedMetric);

            if (hide) {
                return acc;
            }

            const colId = generateMetricColId(ColumnTypes.AllDimensionTotal, metric.kind);
            const savedWidth = getSavedWidth(colId, currentLayout);

            acc.push({
                headerName: metric.displayName,
                headerClass: ({ column }) => {
                    const totalColumnGroup = column.columnApi.getColumnGroup(GRAND_TOTAL_COLS);
                    if (isFirstColumnInGroup(column, true, totalColumnGroup)) {
                        return "borderLeftClass";
                    }

                    return "";
                },
                colId: colId,
                width: savedWidth ?? getAgGridColumnWidth({ text: metric.displayName }),
                hide,
                editable: (params: EditableCallbackParams) => isMetricCellEditable(params, false),
                suppressMenu: true,
                suppressColumnsToolPanel: true,
                suppressMovable: true,
                type: ColumnTypes.AllDimensionTotal,
                valueGetter: (getterParams: ValueGetterParams) => {
                    // Add case for CDP bulk row to use passed in value getter creator for units and avg units metric columns
                    const valueGetter = metricsValueGetterCreator({
                        deliveryId: null,
                        metric,
                        doors: sortedDoors,
                        totalType: MetricsTotalType.GrandTotalMetric,
                        filteredDeliveriesForAssortmentSet,
                        filteredDoorsForAssortmentSet,
                        useSize: shouldUseSizeParam(useSize, getterParams),
                        rootGridApi,
                    });

                    if (typeof valueGetter === "string") {
                        return valueGetter;
                    }
                    return valueGetter(getterParams);
                },
                valueSetter: grandTotalColumnValueSetterCreator(getEntryType(metric)),
                valueFormatter: getMetricsValueFormatter({
                    kind: metric.kind,
                    numberFormat: metric.numberFormat,
                }),
                cellRendererSelector: getCellRendererSelector(null, RowRenderers.CUSTOM_PINNED_ROW_RENDERER),
                cellRendererParams: {
                    metric,
                    splitValue: true,
                    dimensionIds:
                        globalGroups.length > 0
                            ? [globalGroups.map((globalGroup) => globalGroup.id), sortedDoors.map((door) => door.id)]
                            : [sortedDoors.map((door) => door.id)],
                },
                cellStyle: (params) => {
                    const { columnApi, colDef } = params;
                    const column = columnApi.getColumn(colDef.colId);
                    const totalColumnGroup = columnApi.getColumnGroup(GRAND_TOTAL_COLS);
                    const shouldRenderLeftSeparator = isFirstColumnInGroup(column, true, totalColumnGroup);

                    const backgroundColor = isMetricCellEditable(params, false)
                        ? { backgroundColor: "#EBF6FD" }
                        : { backgroundColor: theme.grid.headerBackgroundColor, color: "#a5a0a0" };

                    if (shouldRenderLeftSeparator) {
                        return merge(true, backgroundColor, cellClassForLeftSeparator);
                    }

                    return backgroundColor;
                },
                sortable: !options.isServerSide,
            });

            return acc;
        }, []);

        /**
         * Generate Grand Total Cols for doors and attributes
         * To group doors by attributes, `buildDoorTree` is re-used
         * with a dummy `globalGroup` with a special ID of `null`.
         *
         * - `globalGroup.id === null` distinguishes & identifies these columns in metrics formula calculations
         */
        let grandTotalBreadcrumbs;
        [doorTotalColumns, grandTotalBreadcrumbs] = buildDoorTree(
            { ...options, editable: false, movable: false },
            { id: null, external_id: "", name: "", active: true, fields: {} },
            sortedDoors,
            classifications,
            0,
            shownDoors,
            MetricsTotalType.GrandTotalMetric,
            [],
            filteredDeliveriesForAssortmentSet,
            filteredDoorsForAssortmentSet
        );
    } else {
        // For merchant without metrics, it will be only the grand total column.

        grandTotalCols = [
            {
                headerClass: ({ column }) => {
                    if (isFirstColumnInGroup(column, false)) {
                        return "borderLeftClass";
                    }

                    return "";
                },
                headerName: formatMessage("Total"),
                type: ColumnTypes.AllDimensionTotal,
                filter: false,
                cellRendererSelector: getCellRendererSelector(null, RowRenderers.CUSTOM_PINNED_ROW_RENDERER),
                // use getCellRendererParamsFn if you use more than one cell renderers
                cellRendererParams: {
                    kind: Kinds.TOTAL_ALLOCATION_DIMENSIONS,
                    allocationReferenceFields: {
                        retailFieldName: undefined,
                        wholesaleFieldName: undefined,
                    },
                    doors: sortedDoors,
                    currency: merchantCurrency,
                    getTotalHelper: (planData, gridContext) => {
                        return getTotal({
                            colType: ColumnTypes.AllDimensionTotal,
                            doorTotals: gridContext.doorTotals,
                            totalType: planData.totalType,
                        });
                    },
                    getPlanHelper: (planData, gridContext) => {
                        return getPlan({
                            colType: ColumnTypes.AllDimensionTotal,
                            groupId: "",
                            reviewPlan: planData.plan,
                            doorTotals: gridContext.doorTotals,
                            doors: gridContext.doors,
                            totalType: planData.totalType,
                        });
                    },
                },
                pinnedRowValueFormatter: pinnedValueFormatter,
                width: 90,
                editable: false,
                suppressMenu: true,
                suppressColumnsToolPanel: true,
                suppressMovable: true,
                cellClass: "allocationDimensionTotal",
                cellStyle: ({ columnApi, colDef }) => {
                    const column = columnApi.getColumn(colDef.colId);
                    if (isFirstColumnInGroup(column, false)) {
                        return merge(true, cellClassToAlignRightNotEditable, cellClassForLeftSeparator);
                    }

                    return cellClassToAlignRightNotEditable;
                },
                valueGetter: valueGetterCreator(creatorParams),
                valueFormatter: (params) => (params.value ? Number(params.value.toFixed(1)).toString() : undefined),
                colId: `${ColumnTypes.AllDimensionTotal}_1`,
            },
        ];

        doorTotalColumns = sortedDoors.reduce((acc: any[], door: IDoor, index: number) => {
            const creatorParamsForSecondaryTotalCols = {
                // pull only the relevant doors in each of the deliveries in each door total column's breadcrumb.
                breadcrumb: totalCols.filter((col) => (col.length > 1 ? col[1] === door.id : col[0] === door.id)),
                isTotalColumn: true,
            };
            // Generic col def for total column.
            acc.push({
                headerName: door.label,
                type: ColumnTypes.AllDimensionTotal,
                cellRendererSelector: getCellRendererSelector(null, RowRenderers.CUSTOM_PINNED_ROW_RENDERER),
                pinnedRowValueFormatter: pinnedValueFormatter,
                filter: false,
                cellRendererParams: {
                    kind: Kinds.TOTAL_ALLOCATION_DIMENSIONS,
                    allocationReferenceFields: {
                        retailFieldName: door.retail.fieldName || "us_retail",
                        wholesaleFieldName: door.wholesale.fieldName || "us_cost",
                    },
                    splitValue: true,
                    currency: undefined,
                    groupId: ColumnTypes.NestedDimensionTotal,
                    doorId: door.id,
                    getTotalHelper: (planData, gridContext) => {
                        return getTotal({
                            colType: ColumnTypes.NestedDimensionTotal,
                            doorId: door.id,
                            doorTotals: gridContext.doorTotals,
                            totalType: planData.totalType,
                        });
                    },
                    getPlanHelper: (planData) => {
                        return getPlan({
                            colType: ColumnTypes.NestedDimensionTotal,
                            doorId: door.id,
                            reviewPlan: planData.plan,
                            totalType: planData.totalType,
                        });
                    },
                },
                width: 90,
                editable: false,
                suppressMenu: true,
                suppressColumnsToolPanel: true,
                suppressMovable: true,
                cellClass: "allocationDimensionTotal",
                cellStyle: cellClassToAlignRightNotEditable,
                valueGetter: valueGetterCreator(creatorParamsForSecondaryTotalCols),
                colId: `${ColumnTypes.NestedDimensionTotal}_${index}`,
                columnGroupShow: "open",
            });
            return acc;
        }, []);
    }

    const GRAND_TOTAL_COLS = "grand_total_cols";
    const totalColGroup: ColGroupDef = {
        headerName: formatMessage("Total"),
        headerClass: "borderLeftClass",
        children: [...grandTotalCols, ...doorTotalColumns],
        groupId: GRAND_TOTAL_COLS,
        suppressColumnsToolPanel: true,
    };

    // All columns in the grid = total group columns + individual door column defs.
    const allColDefs = [totalColGroup, ...colDefs];

    if (currentLayout && currentLayout.length) {
        return reconcileTrees(currentLayout, allColDefs);
    }

    return allColDefs;
};

export const parseDoorCollection = memoize(
    (
        doorsCollection: IAllocationDimensionValue[] | List<IAllocationDimensionValue>,
        displayProperties: ISchemaKey[] | List<ISchemaKey>,
        formulaFields: IDisplayFields
    ): IDoor[] => {
        const normalisedDoorsCollection: IAllocationDimensionValue[] = Iterable.isIterable(doorsCollection)
            ? (doorsCollection as List<IAllocationDimensionValue>).toJS()
            : doorsCollection || [];
        const normalisedDisplayProperties = Iterable.isIterable(displayProperties)
            ? (displayProperties as List<ISchemaKey>).toJS()
            : displayProperties;
        const doors = [];

        normalisedDoorsCollection.forEach((door) => {
            if (door.included && door.active) {
                const fields = door.fields;
                const retailFieldName = lodashGet(door, "fields.allocation_retail_price.string_value");
                const wholesaleFieldName = lodashGet(door, "fields.allocation_cost_price.string_value");
                const totalConversionRate = !isNaN(Number(lodashGet(door, "fields.total_conversion_rate.string_value")))
                    ? Number(lodashGet(door, "fields.total_conversion_rate.string_value")) === 0
                        ? 1
                        : Number(lodashGet(door, "fields.total_conversion_rate.string_value"))
                    : 1;
                const groupedDoorsNumber = lodashGet(door, Door.FIELDS.NUMBER_OF_DOORS)
                    ? +lodashGet(door, Door.FIELDS.NUMBER_OF_DOORS)
                    : undefined;
                const doorNotes = lodashGet(door, "fields._door_notes.string_value");
                doors.push({
                    id: door.id,
                    label: door.name,
                    value: door.external_id,
                    retail: {
                        fieldName: retailFieldName,
                        currency: {
                            symbol: "$",
                            decimal_places: 2,
                        },
                        field: formulaFields[retailFieldName] ? formulaFields[retailFieldName].field.toJS() : undefined,
                        formula: formulaFields[retailFieldName]
                            ? formulaFields[retailFieldName].field.get("formula")
                            : undefined,
                    },
                    wholesale: {
                        fieldName: wholesaleFieldName,
                        currency: {
                            symbol: "$",
                            decimal_places: 2,
                        },
                        field: formulaFields[wholesaleFieldName]
                            ? formulaFields[wholesaleFieldName].field.toJS()
                            : undefined,
                        formula: formulaFields[wholesaleFieldName]
                            ? formulaFields[wholesaleFieldName].field.get("formula")
                            : undefined,
                    },
                    classifications: getClassifications(normalisedDisplayProperties, fields),
                    totalConversionRate,
                    doorNotes,
                    groupedDoorsNumber,
                });
            }
        });

        return doors;
    }
);

export const DOOR_GROUP_QUERY_KEY = "dg";
export enum DoorGroupParams {
    Show = "s",
    Classification = "c",
    ClassificationLabel = "cl",
    Expand = "e",
    ClassificationDir = "dir",
    ClassificationHide = "h",
    AllocationIds = "a",
    Delimiter = "d",
    PlanMetrics = "pm",
    ShowPlanMetrics = "spm",
    ShowTotals = "st",
    ActiveValues = "dc",
    DoorsSortDir = "dcs",
    Metrics = "metrics",
    ActiveAttributes = "aa",
}

export interface IDoorGroupsQueryParams {
    [DoorGroupParams.Show]?: 0 | 1;
    [DoorGroupParams.Classification]?: string;
    [DoorGroupParams.ClassificationLabel]?: string;
    [DoorGroupParams.Expand]?: {
        [groupId: string]: 1 | 0;
    };
    [DoorGroupParams.ClassificationDir]?: Order;
    [DoorGroupParams.ClassificationHide]?: string[];
    [DoorGroupParams.AllocationIds]?: string[];
    [DoorGroupParams.Delimiter]?: string;
    [DoorGroupParams.PlanMetrics]?: string[];
    [DoorGroupParams.ShowTotals]?: boolean;
    [DoorGroupParams.ShowPlanMetrics]?: boolean;
    [DoorGroupParams.Metrics]?: Metrics;
    [DoorGroupParams.ActiveValues]?: IAllocationDimensionColumns;
    [DoorGroupParams.ActiveAttributes]?: IDoorGroup[];
}
// Classification Here means attributes
export enum DimensionParamsActionType {
    ShowDoorsGroup = "showDoorsGroup",
    ShowClassification = "showClassification",
    ShowClassificationLabel = "showClassificationLabel",
    ExpandGroup = "expandGroup",
    CollapseGroup = "collapseGroup",
    ChangeDirection = "changeDirection",
    HideClassification = "hideClassificaton",
    SetAllocationIds = "setAllocationIds",
    SetDelimiter = "setDelimiter",
    SelectPlanMetric = "selectPlanMetric",
    ShowDoorTotals = "showDoorTotals",
    ShowPlanMetrics = "showPlanMetrics",
    SetMetrics = "setMetrics",
    SetActiveAttributes = "setActiveAttributes",
    SetActiveValues = "setActiveValues",
}

// tslint:disable-next-line: interface-over-type-literal
export type DimensionParamsAction = {
    [DimensionParamsActionType.ShowDoorsGroup]: {
        type: DimensionParamsActionType.ShowDoorsGroup;
        value: boolean | undefined;
    };
    [DimensionParamsActionType.ExpandGroup]: {
        type: DimensionParamsActionType.ExpandGroup;
        value: string;
        inUrl?: boolean;
    };
    [DimensionParamsActionType.CollapseGroup]: {
        type: DimensionParamsActionType.CollapseGroup;
        value: string;
    };
    [DimensionParamsActionType.ShowClassification]: {
        type: DimensionParamsActionType.ShowClassification;
        value: string | undefined;
    };
    [DimensionParamsActionType.ShowClassificationLabel]: {
        type: DimensionParamsActionType.ShowClassificationLabel;
        value: string | undefined;
    };
    [DimensionParamsActionType.HideClassification]: {
        type: DimensionParamsActionType.HideClassification;
        value: string[] | undefined;
    };
    [DimensionParamsActionType.ChangeDirection]: {
        type: DimensionParamsActionType.ChangeDirection;
        value: Order | undefined;
    };
    [DimensionParamsActionType.SetAllocationIds]: {
        type: DimensionParamsActionType.SetAllocationIds;
        value: string[] | undefined;
    };
    [DimensionParamsActionType.SetDelimiter]: {
        type: DimensionParamsActionType.SetDelimiter;
        value: string | undefined;
    };
    [DimensionParamsActionType.SelectPlanMetric]: {
        type: DimensionParamsActionType.SelectPlanMetric;
        value: string[] | undefined;
    };
    [DimensionParamsActionType.ShowDoorTotals]: {
        type: DimensionParamsActionType.ShowDoorTotals;
        value: boolean | undefined;
    };
    [DimensionParamsActionType.SetMetrics]: {
        type: DimensionParamsActionType.SetMetrics;
        value: Metrics | undefined;
    };
    [DimensionParamsActionType.ShowPlanMetrics]: {
        type: DimensionParamsActionType.ShowPlanMetrics;
        value: boolean | undefined;
    };
    [DimensionParamsActionType.SetActiveAttributes]: {
        type: DimensionParamsActionType.SetActiveAttributes;
        value: IDoorGroup[] | undefined;
    };
    [DimensionParamsActionType.SetActiveValues]: {
        type: DimensionParamsActionType.SetActiveValues;
        value: IAllocationDimensionColumns | undefined;
    };
};

export type ISaveStateToDimensionParamsActions = ValueOf<DimensionParamsAction> | Array<ValueOf<DimensionParamsAction>>;

/**
 * This function is an intermediate function to make existing way of configuring door group params work with `DimensionsPanelReduxState`
 * Removing it will require big effort to replace the usage with something else
 * @param saveStateToQueryParamsActions
 * @param existingState
 * @returns
 */
export const processActionsToDimensionParams = (
    saveStateToQueryParamsActions: ISaveStateToDimensionParamsActions,
    existingState?: DimensionsPanelReduxState
) => {
    const actions = Array.isArray(saveStateToQueryParamsActions)
        ? saveStateToQueryParamsActions
        : [saveStateToQueryParamsActions];
    let mutatedState = lodashCloneDeep(existingState ?? getDimensionsPanelEmptyState());
    actions.forEach((action) => {
        switch (action.type) {
            case DimensionParamsActionType.ShowDoorsGroup:
                mutatedState = lodashSet(mutatedState, "showDoors", action.value ? true : false);
                break;
            case DimensionParamsActionType.ShowClassification:
                mutatedState.attributes.forEach((attribute) => {
                    if (attribute.id === action.value) {
                        attribute.selected = true;
                        attribute.values.forEach((attributeValue) => {
                            attributeValue.selected = true;
                        });
                    } else {
                        attribute.selected = false;
                        attribute.values.forEach((attributeValue) => {
                            attributeValue.selected = false;
                        });
                    }
                });
                break;
            case DimensionParamsActionType.ShowClassificationLabel:
                mutatedState.attributes.forEach((attribute) => {
                    if (attribute.id === action.value) {
                        attribute.showLabelInHeader = true;
                    } else {
                        attribute.showLabelInHeader = false;
                    }
                });
                break;
            case DimensionParamsActionType.CollapseGroup:
                mutatedState.collapsedGroupIds = mutatedState.collapsedGroupIds
                    .filter((groupId) => groupId !== action.value)
                    .concat([action.value]);
                break;
            case DimensionParamsActionType.ExpandGroup:
                mutatedState.collapsedGroupIds = mutatedState.collapsedGroupIds.filter(
                    (groupId) => groupId !== action.value
                );
                break;
            case DimensionParamsActionType.ChangeDirection:
                if (action.value) {
                    mutatedState.attributes.forEach((attribute) => {
                        attribute.sort = action.value;
                    });
                }
                break;
            case DimensionParamsActionType.HideClassification:
                mutatedState.attributes.forEach((attribute) => {
                    if (attribute.selected) {
                        attribute.values.forEach((attributeValue) => {
                            if (!(action.value ?? []).includes(attributeValue.id)) {
                                attributeValue.selected = true;
                            } else {
                                attributeValue.selected = false;
                            }
                        });
                    }
                });
                break;
            case DimensionParamsActionType.SetDelimiter:
                mutatedState.delimiter = action.value || GROUP_ID_DELIMITER;
                break;
            case DimensionParamsActionType.SelectPlanMetric:
                // Legacy Metrics. We'll set values, subtotal and totals ON
                if (action.value && action.value.length) {
                    mutatedState.metrics.showAssortmentPlan = true;
                    mutatedState.metrics.values = action.value.reduce((acc, legacyMetricType) => {
                        const metricKind = getMetricKindByLegacyType(legacyMetricType as TotalTypes);
                        if (metricKind) {
                            // We only want to set this to `true` when there is actual metric
                            acc.push({
                                showValue: true,
                                showTotals: true,
                                showSubtotals: true,
                                kind: metricKind,
                                displayName: getTotalTypesLabel(legacyMetricType as TotalTypes),
                                isTargetMetric: false,
                            });
                        }
                        return acc;
                    }, []);
                } else {
                    mutatedState.metrics.showAssortmentPlan = false;
                }
                break;
            case DimensionParamsActionType.ShowDoorTotals:
                mutatedState.metrics.showAssortmentTotals = !!action.value;
                break;
            case DimensionParamsActionType.SetMetrics:
                mutatedState.metrics.values = action.value;
                break;
            case DimensionParamsActionType.ShowPlanMetrics:
                mutatedState.metrics.showAssortmentPlan = action.value;
                break;
            case DimensionParamsActionType.SetActiveValues:
                action.value.forEach((activeValueView, idx) => {
                    if (mutatedState.dimensions[idx]) {
                        // This will be mutated if any of the value is selected
                        mutatedState.dimensions[idx].selected = false;
                        mutatedState.dimensions[idx].sort = activeValueView.direction ?? Order.Ascending;

                        mutatedState.dimensions[idx].values.forEach((dimensionValue) => {
                            if (!activeValueView.values) {
                                // When `activeValueView.values` is falsy, treat it as all dimension values being ON
                                dimensionValue.selected = true;
                            } else if (activeValueView.values.length === 0) {
                                // When `activeValueView.values` is empty array, treat it as all dimension values being FALSE
                                dimensionValue.selected = false;
                            } else if (activeValueView.values) {
                                dimensionValue.selected = activeValueView.values.includes(dimensionValue.id);
                            }

                            if (dimensionValue.selected) {
                                mutatedState.dimensions[idx].selected = true;
                            }
                        });

                        if (mutatedState.dimensions[idx].sort === Order.Custom && activeValueView.values) {
                            // Sort values based on passed-in values. This is not an ideal because we loop twice (above block and this block).
                            // It's done this way for readibility. Practically, number of attributes and values will be small, therefore, performance should not be a concern.
                            customSort(
                                mutatedState.dimensions[idx].values,
                                activeValueView.values,
                                (value) => value.id
                            );
                        }
                    }
                });
                break;
            case DimensionParamsActionType.SetActiveAttributes:
                for (const activeAttributeView of action.value) {
                    const mutatedAttribute = mutatedState.attributes.find(
                        (attribute) => attribute.id === activeAttributeView.field
                    );
                    if (mutatedAttribute) {
                        // `action.value` always contains active attribute
                        mutatedAttribute.selected = true;
                        mutatedAttribute.sort = activeAttributeView.direction;

                        mutatedAttribute.values = mutatedAttribute.values.map((activeValue) => {
                            if (!activeAttributeView.values) {
                                // When `activeValueView.values` is falsy, treat it as all attribute values being ON
                                activeValue.selected = true;
                            } else if (activeAttributeView.values.length === 0) {
                                // When `activeValueView.values` is empty array, treat it as all attribute values being FALSE
                                activeValue.selected = false;
                            } else if (activeAttributeView.values) {
                                activeValue.selected = activeAttributeView.values.includes(activeValue.id);
                            }

                            return activeValue;
                        });

                        if (mutatedAttribute.sort === Order.Custom && activeAttributeView.values) {
                            // Sort values based on passed-in values. This is not an ideal because we loop twice (above block and this block).
                            // It's done this way for readibility. Practically, number of attributes and values will be small, therefore, performance should not be a concern.
                            mutatedAttribute.values.sort((a, b) => {
                                const indexA = activeAttributeView.values.findIndex((value) => value === a.id);
                                const indexB = activeAttributeView.values.findIndex((value) => value === b.id);

                                if (indexA === -1) {
                                    return 1;
                                }

                                if (indexA < indexB || indexB === -1) {
                                    return -1;
                                }

                                return 0;
                            });
                        }
                    }
                }
                break;
            default:
                break;
        }
    });
    return mutatedState;
};

interface ILegacyParsedDoorGroupParams {
    attribute?: any;
    attributeLabel?: string;
    attributeDirection?: Order;
    attributeHiddenValues?: string[];
    legacyPlanMetrics?: string[];
}

/**
 * Helper function to parse commonly used properties for legacy usage
 * Legacy usage only allows enabling of single attribute
 */
export const legacy_parseDimensionParams = memoize(
    (dimensionParams: DimensionsPanelReduxState): ILegacyParsedDoorGroupParams => {
        const singleActiveAttribute = dimensionParams.attributes.find((attribute) => attribute.selected);
        const attributeHiddenValues =
            singleActiveAttribute?.values
                .filter((attributeValue) => !attributeValue.selected)
                .map((attributeValue) => attributeValue.id) ?? [];
        const attributeLabel = dimensionParams.attributes.find((attribute) => attribute.showLabelInHeader)?.id;
        const attributeDirection = singleActiveAttribute?.sort;
        const legacyPlanMetrics = dimensionParams.metrics.values.map((metric) =>
            getLegacyTypeByMetricsKind(metric.kind)
        );

        return {
            attribute: singleActiveAttribute?.id,
            attributeLabel,
            attributeHiddenValues,
            attributeDirection,
            legacyPlanMetrics,
        };
    }
);

export const filtersToShowToFiltersToHideArray = (
    filterSelectOptions: Array<{ label: string; value: string }>,
    filterSelect: { [key: string]: boolean }
): string[] => {
    const filtersToHide = filterSelectOptions.reduce((acc, value) => {
        const stringValue = value.value;
        if (!filterSelect[stringValue]) {
            acc.push(stringValue);
        }
        return acc;
    }, []);
    return filtersToHide;
};

export const filtersListToFiltersToShowObject = (
    filterSelectOptions: Array<{ label: string; value: string }>,
    optionsToHide: string[] = []
): { [key: string]: boolean } => {
    const filterSelect = filterSelectOptions.reduce((acc, optionValue) => {
        const stringValue = optionValue.value;
        if (!optionsToHide.includes(stringValue)) {
            acc[stringValue] = true;
        }
        return acc;
    }, {});
    return filterSelect;
};
/**
 * Filter out door based on date/door drop-down filter and doors grouping dialog filter
 *
 * @param door - Current door being evaluated
 * @param activeClassification - Current door classification category being evaluated
 * @param hiddenClassificationValues - List of hidden classification values
 * @param collectionId - collection id for rollup logic
 * @param attributeFilters - Key - array value object of attribute filters

 *
 * @returns true if classification in hidden list or door not in door-filter (ONLY if door-filter populated)
 */
export const isDoorInClassification = (
    door: IDoor,
    {
        activeClassification,
        hiddenClassificationValues,
        collectionId,
        attributeFilters,
    }: {
        activeClassification: string;
        hiddenClassificationValues: any;
        collectionId?: string;
        attributeFilters?: IAttributeFilters;
    }
): boolean => {
    const activeClassificationValue = door?.classifications?.[activeClassification] || "";

    const attributeFilterKeys = Object.keys(attributeFilters || {});
    const doorHasClassification =
        attributeFilterKeys.length === 0
            ? true
            : attributeFilterKeys.every((key) => attributeFilters[key].includes(door?.classifications?.[key] || ""));

    if (Array.isArray(hiddenClassificationValues)) {
        return !hiddenClassificationValues?.includes(activeClassificationValue) && doorHasClassification; // classification is not hidden
    }

    const classificationKey = collectionId
        ? `${activeClassification}${SR_CUSTOM_DOOR_GROUPING_DELIMITER}${collectionId}`
        : activeClassification;
    return (
        !hiddenClassificationValues?.[classificationKey]?.includes(activeClassificationValue) && doorHasClassification
    );
};

/**
 * Test if dimension values are in filter if filter is populated
 *
 * @param dimensionFilters - filter that contains specified dimensions values
 * @param dimensions - dimension internal-id
 *
 * @returns true if all dimensions internal-ids are in all populated filter
 *
 */
export const isDimensionInFilters = (dimensionFilters: string[][], dimensions: string[]): boolean => {
    let dimensionInFilter = true;
    for (const dimIndex in dimensionFilters) {
        const filter = dimensionFilters?.[dimIndex];
        const dimensionValue = dimensions?.[dimIndex];
        // filter populated and dimension internal-id exists then test if value in filter
        if (filter?.length && dimensionValue) {
            dimensionInFilter = dimensionInFilter && filter.includes(dimensionValue);
        }
    }
    return dimensionInFilter;
};

export const getVisibleDoors = (
    doors: IDoor[],
    dimensionParams: DimensionsPanelReduxState,
    gridParams: IGridParamsQueryParam,
    excludeAttributeFilter: boolean = false
) => {
    const { attribute, attributeHiddenValues } = legacy_parseDimensionParams(dimensionParams);
    const dimensionFilters = getDimensionFilters(gridParams);
    const attributeFilters = !excludeAttributeFilter && getAttributeFilters(gridParams);

    return doors.filter(
        (door) =>
            isDoorInClassification(door, {
                activeClassification: attribute,
                hiddenClassificationValues: attributeHiddenValues,
                attributeFilters,
            }) && isDimensionInFilters(dimensionFilters, [null, door.id]) // door in selected classifications // and door-dimension in filters, date-dimension unavailable (only in rows/bulks)
    );
};

/**
 * Retrieve dimensionFilter inside of gridParam.filter
 *
 * @param gridParams - Grid parameters containing filter with meta-filter embedded into filterTo field
 * @returns - double array that contains selected external/interal ids indexed by allocation_dimension ids [["0","1"], ["1","2"]]
 */
export const getDimensionFilters = memoize(
    (gridParams: IGridParamsQueryParam): IDimensionFilter[] => {
        // use special column to store meta-filter
        const dimensionFilterString = (
            gridParams?.filter?.[GridParamType.DimensionFilters] as IFilterConditionQueryParam
        )?.filterTo as string;
        // guarantee date/door is not null/undefined
        let dimensionFilter;
        try {
            if (dimensionFilterString) {
                dimensionFilter = JSON.parse(dimensionFilterString);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Cannot parse JSON", e);
        }

        // guarantee non-null/undefined variables
        return dimensionFilter || [];
    },
    (newArgs, oldArgs) => {
        return (
            newArgs[0]?.filter?.[GridParamType.DimensionFilters]?.filterTo ===
            oldArgs[0]?.filter?.[GridParamType.DimensionFilters]?.filterTo
        );
    }
);

/**
 * Retrieve attributeFilter inside of gridParam.filter
 *
 * @param gridParams - Grid parameters containing filter with meta-filter embedded into filterTo field
 * @returns - list of objects that contains selected attributes and its selected values
 */
export const getAttributeFilters = memoize(
    (gridParams: IGridParamsQueryParam): IAttributeFilters => {
        // use special column to store meta-filter
        const attributeFilterString = (
            gridParams?.filter?.[GridParamType.CustomAttributeFilter] as IFilterConditionQueryParam
        )?.filterTo as string;
        // guarantee date/door is not null/undefined
        let attributeFilter;
        try {
            if (attributeFilterString) {
                attributeFilter = JSON.parse(attributeFilterString);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Cannot parse JSON", e);
        }

        // guarantee non-null/undefined variables
        return attributeFilter || {};
    },
    (newArgs, oldArgs) => {
        return (
            newArgs[0]?.filter?.[GridParamType.CustomAttributeFilter]?.filterTo ===
            oldArgs[0]?.filter?.[GridParamType.CustomAttributeFilter]?.filterTo
        );
    }
);

export enum DimensionProps {
    Id = "id",
    Classification = "group",
    ClassificationLabel = "group_label",
    Show = "show",
    ClassificationDir = "sort_direction",
    ClassificationHide = "hidden_group_values",
    PlanMetrics = "plan_metrics",
    ShowTotals = "show_totals",
    ShowPlan = "show_plan",
    ActiveValues = "active_values",
    ActiveAttributes = "active_attributes",
}

export interface IActiveValues {
    values: Array<{ id: string }>;
    order: DimensionOrder;
    all_values?: boolean;
}

export interface IActiveAttributes {
    attributes: Array<{
        field_name: string;
        values: Array<{
            string_value: string;
        }>;
        order: DimensionOrder;
    }>;
    order: DimensionOrder;
}

export enum DimensionOrder {
    Ascending = "ASCENDING",
    Descending = "DESCENDING",
    Custom = "CUSTOM",
    Unordered = "UNORDERED",
}

export enum ExpandedColumnsProps {
    Delimiter = "delimiter",
    Columns = "columns",
}

export const sortForGridDictionary = {
    [DimensionOrder.Ascending]: "asc",
    [DimensionOrder.Descending]: "desc",
};

export interface IDimension {
    [DimensionProps.Id]: string;
    [DimensionProps.Classification]?: string;
    [DimensionProps.ClassificationLabel]?: string;
    [DimensionProps.Show]?: boolean;
    [DimensionProps.ClassificationDir]?: string;
    [DimensionProps.ClassificationHide]?: string[];
    [DimensionProps.PlanMetrics]?: string[];
    [DimensionProps.ActiveValues]?: IActiveValues;
    [DimensionProps.ActiveAttributes]?: IActiveAttributes;
}

export interface IExpandedColumns {
    [ExpandedColumnsProps.Delimiter]?: string;
    [ExpandedColumnsProps.Columns]?: { [key: string]: boolean };
}

export interface IServerDimensionPreference {
    dimensions: IDimension[];
    expanded_columns: IExpandedColumns;
}

export const isDoorColumn = (columnName: string, colType?: string) => {
    return Object.values(ColumnTypes).some((type) => columnName?.includes(type) || colType === type);
};

/* Function to fetch the saved width of a column before rendering it */
export const getSavedWidth = (colId: string, currentLayout: IPreference[]) => {
    return currentLayout?.find((layout) => {
        return layout.field_name === colId;
    })?.grid_column?.width;
};

// Get fields related to door and its formula dependencies fields
export const getDoorsRelatedFields = memoize((doors) => {
    const relatedFieldsMap = doors.reduce((acc, door) => {
        acc[door.retail.fieldName] = true;
        acc[door.wholesale.fieldName] = true;
        const formulas = [];

        door.retail.formula && formulas.push(door.retail.formula);
        door.wholesale.formula && formulas.push(door.wholesale.formula);

        // Collect field dependencies of formula
        formulas.forEach((formula) => {
            try {
                const formulaNode = collectionMathJs.parse(formula);
                formulaNode.traverse((node) => {
                    // if symbol and not built-in or custom
                    if (mathJsSymbolIsField(node, collectionMathJs)) {
                        const field: string = node.toString();
                        acc[field] = true;
                    }
                });
            } catch (error) {
                Sentry.captureException(error);
                // eslint-disable-next-line no-console
                console.error(error);
            }
        });

        return acc;
    }, {});

    return Object.keys(relatedFieldsMap);
});

export const getClassificationLabelBySchema = (
    classificationValue?: string,
    schema?: ISchemaKey,
    options?: { merchantPurchaseOrderDoorGroups?: IPurchaseOrderDoorGroup[] }
): string => {
    if (schema?.field_name === PURCHASE_ORDER_DOOR_GROUP_FIELD && options?.merchantPurchaseOrderDoorGroups) {
        const merchantPurchaseOrderDoorGroup = options.merchantPurchaseOrderDoorGroups.find(
            (_merchantPurchaseOrderDoorGroup) => _merchantPurchaseOrderDoorGroup.id === classificationValue
        );
        return merchantPurchaseOrderDoorGroup?.name ?? classificationValue;
    }

    return classificationValue;
};

const getIndexMapByExternalId = memoize((doors: IDoor[]) => {
    return doors.reduce((acc, door, index) => {
        acc[door.value] = index;
        return acc;
    }, {});
});

export const findDoorByExternalId = (externalId: string, doors: IDoor[]): IDoor | undefined => {
    const doorIndexMap = getIndexMapByExternalId(doors);

    const index = doorIndexMap[externalId];
    if (index === undefined) {
        return undefined;
    }

    return doors[index];
};

export const isAnyDoorSelected = (dimensions: Dimension[]) =>
    lodashGet(last(dimensions), "values")?.filter((dimensionValue) => dimensionValue.selected).length !== 0;
/**
 * Due to performance issue (https://nuorder-inc.atlassian.net/browse/NU-24938), we'll not calculate totals through delta changes for > 2,000 mutations (2 batches of 1,000 mutations).
 * Instead, we'll recalculate the whole thing in webworker to defer looping through the whole changes.d
 */
export const shouldCalculateTotalByDelta = (count: number = 0, overrideThreshold?: number) => {
    return count <= (overrideThreshold ?? 2);
};

export const shouldUseSizeParam = (useSize: boolean, getterParams: ValueGetterParams): boolean => {
    return useSize && getterParams.node.data.type !== BULK_IDENTIFIER;
};

const getFilteredDeliveriesArray = (filteredDeliveries: Set<string>) => {
    if (filteredDeliveries) {
        return Array.from(filteredDeliveries);
    }
    return [];
};

const getEntryType = (metric: Metric) => {
    return metric.kind === MetricsKind.AVERAGE_UNITS ? UnitsEntryType.GroupAverage : UnitsEntryType.Total;
};
